import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { format, rbac } from '@dha-nexus/shared';
import { useApi, useMutation } from '../../lib/useApi.js';
import { humanizeEnum, sourceSystemLabel } from '../../lib/fieldLabels.js';
import { api } from '../../lib/apiClient.js';
import { useClientPagination, useUrlPagination } from '../../lib/usePagination.js';
import { prependWorkspaceBreadcrumb } from '../../lib/workspaceContext.js';
import { usePageMetadata } from '../../app/PageMetaContext.jsx';
// The shell page header (distinct from the primitive PageHeader imported below,
// which the list page uses): renders the single body h1 for the detail route
// from the record metadata this page registers (PRIME-SHELL-UX-001 revise 2).
import { PageHeader as ShellPageHeader } from '../../app/PageHeader.jsx';
import { useCurrentUser } from '../../app/CurrentUserContext.jsx';
import {
  PageActionStrip,
  PageHeader,
  Breadcrumbs,
  LoadingSkeleton,
  ErrorState,
  EmptyState,
  HealthIndicator,
  StatusChip,
  DataSourceBadge,
  Sparkline,
  GanttPanel,
  EvidenceList,
  BriefingExportCard,
  AiGroundingChip,
  PageViewerControls,
  StagePipeline,
  toast,
  activityRail,
  KpiHeroBand,
  InlineScoreCell,
  WorkflowPipeline,
  buildPipelineSteps,
  KpiBandSkeleton,
  TableSkeleton,
  BrandedEmptyState,
  bandTone,
  GaugeCard,
  MiniBars,
  FinancialWaterfall,
  StatusDonutMini,
} from '../../components/index.js';
import { ConfirmActionDialog } from '../../components/primitives/ConfirmActionDialog.jsx';
import { MissionThreadPanel } from './MissionThreadPanel.jsx';
import { ProjectArchiveTab } from './ProjectArchiveTab.jsx';
import { ProjectStakeholderReadiness } from './ProjectStakeholderReadiness.jsx';
import { AskFinancialChangeButton } from './AskFinancialChangeButton.jsx';
import { RapidEvidenceDrawer, markRapidGuideStep } from '../rapid/RapidLeadershipUi.jsx';
import { ProjectActionsTab, RiskToActionDrawer } from '../actions/ActionCenterPages.jsx';
import { ProjectKnowledgeRoomTab } from '../knowledge/KnowledgePages.jsx';
import { ProjectResourcesTab } from '../resources/ResourcePages.jsx';
import { buildInformationPaperPath } from '../briefings/reportEntryLinks.js';
import './projectProfile.css';
import './projects.redesign.css';

const PROJECT_LIFECYCLE_STAGES = [
  { id: 'intake', label: 'Intake', matchers: ['intake', 'concept'] },
  {
    id: 'placemat',
    label: 'Placemat',
    matchers: ['placemat', 'business case', 'business_case', 'businesscase'],
  },
  { id: 'scoping', label: 'Scoping', matchers: ['scoping', 'approval', 'scope'] },
  { id: 'diagnose', label: 'Diagnose', matchers: ['diagnose', 'discovery'] },
  {
    id: 'implement',
    label: 'Implement',
    matchers: ['implement', 'implementation', 'execution', 'development', 'integration'],
  },
  {
    id: 'sustain_transition',
    label: 'Sustain/Transition',
    matchers: ['sustainment', 'sustain', 'transition', 'production', 'operate'],
  },
  { id: 'closeout', label: 'Closeout', matchers: ['closeout', 'closure'] },
  { id: 'lessons_learned', label: 'Lessons Learned', matchers: ['lesson'] },
  {
    id: 'archived_completed',
    label: 'Archived/Completed',
    matchers: ['archived', 'archive', 'completed', 'complete', 'closed', 'retirement'],
  },
];

function projectLifecycleStageIdFor(value) {
  const normalized = String(value || '').toLowerCase();
  const match = PROJECT_LIFECYCLE_STAGES.find((stage) =>
    stage.matchers.some((matcher) => normalized.includes(matcher)),
  );
  return match?.id || 'implement';
}

function projectLifecycleStageLabelFor(value) {
  const id = projectLifecycleStageIdFor(value);
  return PROJECT_LIFECYCLE_STAGES.find((stage) => stage.id === id)?.label || value || 'Unspecified';
}

function buildProjectLifecycleStagePipeline(currentStage) {
  const activeId = projectLifecycleStageIdFor(currentStage);
  const activeIndex = PROJECT_LIFECYCLE_STAGES.findIndex((stage) => stage.id === activeId);
  return PROJECT_LIFECYCLE_STAGES.map((stage, index) => ({
    id: stage.id,
    label: stage.label,
    status: index < activeIndex ? 'done' : index === activeIndex ? 'active' : 'upcoming',
  }));
}

// ---- ProjectListPage helpers ------------------------------------------------
// All derived client-side from the already-loaded /projects items array. No
// fabricated values: a metric is only rendered when its source field exists.

function projectOverallHealth(p) {
  const raw = p?.healthScores?.overall ?? p?.health?.overall ?? p?.overallHealth;
  return raw == null || Number.isNaN(Number(raw)) ? null : Number(raw);
}

function isAtRiskProjectStatus(value) {
  return /at.?risk|red|breach|critical|escalat/i.test(String(value || ''));
}

// A gate is "due" when the project carries an active gate summary that has not
// already been approved/completed — i.e. a decision is still owed.
function projectHasGateDue(p) {
  const gate = p?.currentGateSummary;
  if (!gate) return false;
  return !isApprovedGateStatus(gate.status);
}

// Map a single gate status onto the canonical gate decision lifecycle so the
// Current Gate column can show a chevron pipeline instead of a bare chip.
// Denied / returned stays 'blocked' (never collapses to pending).
const GATE_PIPELINE_ORDER = ['submitted', 'inReview', 'decision'];
const GATE_PIPELINE_LABELS = {
  submitted: 'Submitted',
  inReview: 'In Review',
  decision: 'Decision',
};

function buildGatePipelineSteps(status) {
  const s = String(status || '').toLowerCase();
  if (/approv|complete/.test(s)) {
    // Decision reached and cleared — every stage is done.
    return GATE_PIPELINE_ORDER.map((key) => ({
      key,
      label: GATE_PIPELINE_LABELS[key],
      status: 'done',
    }));
  }
  if (/den|reject|return|fail|block/.test(s)) {
    // Decision reached but blocked — flag the decision stage.
    return buildPipelineSteps({
      order: GATE_PIPELINE_ORDER,
      current: 'decision',
      statusByKey: { decision: 'blocked' },
      labels: GATE_PIPELINE_LABELS,
    });
  }
  if (/review|progress/.test(s)) {
    return buildPipelineSteps({
      order: GATE_PIPELINE_ORDER,
      current: 'inReview',
      labels: GATE_PIPELINE_LABELS,
    });
  }
  // pending / scheduled / draft / unknown -> awaiting submission decision
  return buildPipelineSteps({
    order: GATE_PIPELINE_ORDER,
    current: 'submitted',
    labels: GATE_PIPELINE_LABELS,
  });
}

function deriveProjectListKpis(items, pageTotal) {
  const total = pageTotal != null ? pageTotal : items.length;
  const atRisk = items.filter((p) => isAtRiskProjectStatus(p.status)).length;
  const gatesDue = items.filter(projectHasGateDue).length;
  const healthValues = items.map(projectOverallHealth).filter((v) => v != null);
  const avgHealth = healthValues.length
    ? Math.round(healthValues.reduce((sum, v) => sum + v, 0) / healthValues.length)
    : null;

  const cards = [
    {
      id: 'projects-total',
      label: 'Projects in view',
      value: total,
      status: 'blue',
      footer:
        pageTotal != null && pageTotal !== items.length
          ? `${items.length} on this page`
          : 'Active portfolio scope',
    },
    {
      id: 'projects-at-risk',
      label: 'At-risk projects',
      value: atRisk,
      status: atRisk > 0 ? 'brick' : 'sage',
      statusLabel: atRisk > 0 ? 'Attention' : 'Clear',
      footer: atRisk > 0 ? 'Flagged for executive review' : 'No at-risk projects on page',
    },
    {
      id: 'projects-gates-due',
      label: 'Gates awaiting decision',
      value: gatesDue,
      status: gatesDue > 0 ? 'amber' : 'sage',
      statusLabel: gatesDue > 0 ? 'Pending' : 'Clear',
      footer: gatesDue > 0 ? 'Decisions owed at the next forum' : 'No open gate decisions on page',
    },
  ];

  // Avg health is only honest when at least one project exposes a health score.
  if (avgHealth != null) {
    cards.push({
      id: 'projects-avg-health',
      label: 'Avg health',
      value: avgHealth,
      unit: '/100',
      status: bandTone(avgHealth, { good: 80, warn: 50 }),
      sparkline: healthValues.slice(0, 12),
      sparklineTone: bandTone(avgHealth, { good: 80, warn: 50 }),
      footer: `Mean across ${healthValues.length} scored project${healthValues.length === 1 ? '' : 's'}`,
    });
  }

  return cards;
}

function ProjectListGateCell({ projectId, gate }) {
  if (!gate) {
    return <span className="muted">No active gate</span>;
  }
  const steps = buildGatePipelineSteps(gate.status);
  return (
    <div className="profile-list-gate-cell projects-gate-cell">
      <strong>{gate.name || gate.id}</strong>
      <div className="muted small">{gate.decisionForumDate || '—'}</div>
      <WorkflowPipeline
        steps={steps}
        ariaLabel={`Gate decision progress for ${gate.name || gate.id}`}
      />
      <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
        <StatusChip status={gate.status} />
        <Link to={gateReviewRoute(projectId, gate.id)}>Open Gate Review</Link>
      </div>
    </div>
  );
}

export function ProjectListPage() {
  const [params, setParams] = useSearchParams();
  const pager = useUrlPagination(params, setParams, { defaultPageSize: 10 });
  const r = useApi('/projects', { query: { limit: pager.pageSize, offset: pager.offset } });
  if (r.loading)
    return (
      <>
        <PageHeader
          eyebrow="Executive"
          title="Projects"
          subtitle="Active projects across the portfolio."
        />
        <KpiBandSkeleton count={4} />
        <div style={{ marginTop: 16 }}>
          <TableSkeleton rows={6} />
        </div>
      </>
    );
  if (r.error) return <ErrorState error={r.error} />;
  const items = r.data?.data || [];
  const kpiCards = deriveProjectListKpis(items, r.data?.page?.total);
  return (
    <>
      <PageHeader
        eyebrow="Executive"
        title="Projects"
        subtitle="Active projects across the portfolio."
      />
      {items.length === 0 ? (
        <BrandedEmptyState
          icon="📁"
          eyebrow="Executive"
          title="No projects in view"
          description="No active projects match the current portfolio scope. New projects appear here as soon as they enter the lifecycle."
        />
      ) : (
        <>
          <KpiHeroBand eyebrow="Portfolio posture" items={kpiCards} columns={kpiCards.length} />
          <div className="table-scroll projects-table-scroll" style={{ marginTop: 16 }}>
            <table className="table projects-list-table">
              <thead>
                <tr>
                  <th>Project</th>
                  <th>Program</th>
                  <th>Current Gate</th>
                  <th>Stage</th>
                  <th>Health</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {items.map((p) => {
                  const atRisk = isAtRiskProjectStatus(p.status);
                  const health = projectOverallHealth(p);
                  return (
                    <tr
                      key={p.id}
                      className={atRisk ? 'projects-row--at-risk' : undefined}
                      data-at-risk={atRisk ? 'true' : undefined}
                    >
                      <td>
                        {atRisk ? (
                          <span className="projects-risk-ribbon" aria-hidden="true" />
                        ) : null}
                        <Link to={`/projects/${p.id}`}>{p.name}</Link>
                        <div className="muted small">{p.id}</div>
                      </td>
                      <td>{p.programId || '—'}</td>
                      <td>
                        <ProjectListGateCell projectId={p.id} gate={p.currentGateSummary} />
                      </td>
                      <td>
                        <StatusChip status={p.lifecycleStage} />
                      </td>
                      <td>
                        {health != null ? (
                          <InlineScoreCell
                            value={health}
                            max={100}
                            thresholds={{ good: 80, warn: 50 }}
                            ariaLabel={`Overall health ${health} of 100`}
                          />
                        ) : (
                          <HealthIndicator health={undefined} />
                        )}
                      </td>
                      <td>
                        {atRisk ? (
                          <span className="banner projects-status-banner tone-brick">
                            <StatusChip status={p.status} />
                          </span>
                        ) : (
                          <StatusChip status={p.status} />
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
      <PageViewerControls
        page={r.data?.page}
        label="projects"
        pageSize={pager.pageSize}
        onOffsetChange={pager.setOffset}
        onPageSizeChange={pager.setPageSize}
      />
    </>
  );
}

// ---- Project workspace tab consolidation (FS-P3M-3/4, AC-P3M-3) ------------
// The 16 legacy tabs collapse into 7 tab groups. Every legacy capability keeps
// a home as a section inside one of the groups, and every legacy ?tab= value
// is normalized onto its new group/section so existing deep links never break.
export const PROJECT_PROFILE_TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'plan', label: 'Plan' },
  { key: 'delivery', label: 'Delivery' },
  { key: 'money', label: 'Money' },
  { key: 'risk-governance', label: 'Risk & Governance' },
  { key: 'knowledge', label: 'Knowledge' },
  { key: 'ai-products', label: 'AI & Products' },
];

const PROJECT_PROFILE_TAB_KEYS = new Set(PROJECT_PROFILE_TABS.map((tab) => tab.key));

export const PROJECT_PROFILE_TAB_SECTIONS = {
  overview: [
    { key: 'summary', label: 'Summary' },
    { key: 'stakeholders', label: 'Stakeholders & Dates' },
  ],
  plan: [
    { key: 'lifecycle', label: 'Lifecycle' },
    { key: 'roadmap', label: 'Roadmap & Milestones' },
    { key: 'archive', label: 'Archive' },
    // Feature D — the Mission Thread map lives under Plan as a deep-linkable
    // ?tab=journey (resolved to plan/journey) view. Appended last so the plan
    // default section stays 'lifecycle'.
    { key: 'journey', label: 'Mission Thread' },
  ],
  delivery: [
    { key: 'summary', label: 'Summary' },
    { key: 'sprints', label: 'Sprints' },
    { key: 'work-items', label: 'Work Items' },
    { key: 'gates', label: 'Gates' },
    { key: 'resources', label: 'Resources' },
    { key: 'actions', label: 'Actions' },
  ],
  money: [{ key: 'finance', label: 'Finance' }],
  'risk-governance': [
    { key: 'risks', label: 'Risks' },
    { key: 'governance', label: 'Governance' },
    { key: 'kpis', label: 'Mission KPIs' },
  ],
  knowledge: [{ key: 'knowledge-room', label: 'Knowledge Room' }],
  'ai-products': [
    { key: 'ai', label: 'AI' },
    { key: 'briefings', label: 'Briefings' },
    { key: 'audit', label: 'Audit' },
  ],
};

// Legacy 16-tab keys → new { tab, section } home. Exported so tests can prove
// the old→new mapping stays total (capability-parity checklist, 03 §5).
export const PROJECT_PROFILE_LEGACY_TAB_MAP = {
  overview: { tab: 'overview', section: 'summary' },
  stakeholders: { tab: 'overview', section: 'stakeholders' },
  lifecycle: { tab: 'plan', section: 'lifecycle' },
  roadmap: { tab: 'plan', section: 'roadmap' },
  archive: { tab: 'plan', section: 'archive' },
  delivery: { tab: 'delivery', section: 'summary' },
  resources: { tab: 'delivery', section: 'resources' },
  actions: { tab: 'delivery', section: 'actions' },
  finance: { tab: 'money', section: 'finance' },
  risks: { tab: 'risk-governance', section: 'risks' },
  governance: { tab: 'risk-governance', section: 'governance' },
  kpis: { tab: 'risk-governance', section: 'kpis' },
  knowledge: { tab: 'knowledge', section: 'knowledge-room' },
  ai: { tab: 'ai-products', section: 'ai' },
  briefings: { tab: 'ai-products', section: 'briefings' },
  audit: { tab: 'ai-products', section: 'audit' },
};

export const PROJECT_PROFILE_DELIVERY_SECTIONS = PROJECT_PROFILE_TAB_SECTIONS.delivery;

function projectProfileSectionKeys(tabKey) {
  return new Set((PROJECT_PROFILE_TAB_SECTIONS[tabKey] || []).map((section) => section.key));
}

function projectProfileDefaultSection(tabKey) {
  return PROJECT_PROFILE_TAB_SECTIONS[tabKey]?.[0]?.key || null;
}

// Normalize any tab/section query combination — new group keys, legacy 16-tab
// keys, or garbage — onto a valid { tab, section } pair.
export function resolveProjectProfileLocation(searchParams) {
  const requestedTab = searchParams.get('tab');
  const requestedSection = searchParams.get('section');
  let tab = 'overview';
  let section = null;
  if (PROJECT_PROFILE_TAB_KEYS.has(requestedTab)) {
    tab = requestedTab;
  } else if (requestedTab === 'journey') {
    // Feature D — the deep-linkable ?tab=journey resolves to the Plan group's
    // Mission Thread section without adding an 8th top-level tab.
    tab = 'plan';
    section = 'journey';
  } else if (PROJECT_PROFILE_LEGACY_TAB_MAP[requestedTab]) {
    ({ tab, section } = PROJECT_PROFILE_LEGACY_TAB_MAP[requestedTab]);
  }
  const sectionKeys = projectProfileSectionKeys(tab);
  if (requestedSection && sectionKeys.has(requestedSection)) {
    section = requestedSection;
  }
  if (!section || !sectionKeys.has(section)) {
    section = projectProfileDefaultSection(tab);
  }
  return { tab, section };
}

export function getProjectProfileTabFromSearchParams(searchParams) {
  return resolveProjectProfileLocation(searchParams).tab;
}

export function getProjectProfileSectionFromSearchParams(searchParams) {
  return resolveProjectProfileLocation(searchParams).section;
}

export function getProjectProfileDeliverySectionFromSearchParams(searchParams) {
  const { tab, section } = resolveProjectProfileLocation(searchParams);
  return tab === 'delivery' ? section : 'summary';
}

function applyProjectProfileLocation(searchParams, tab, section) {
  const next = new URLSearchParams(searchParams);
  const defaultSection = projectProfileDefaultSection(tab);
  const resolvedSection = projectProfileSectionKeys(tab).has(section) ? section : defaultSection;
  if (tab === 'overview' && resolvedSection === defaultSection) {
    next.delete('tab');
    next.delete('section');
    return next;
  }
  next.set('tab', tab);
  if ((PROJECT_PROFILE_TAB_SECTIONS[tab] || []).length > 1) {
    next.set('section', resolvedSection);
  } else {
    next.delete('section');
  }
  return next;
}

export function buildProjectProfileTabSearchParams(searchParams, nextTab) {
  let target;
  if (PROJECT_PROFILE_TAB_KEYS.has(nextTab)) {
    const current = resolveProjectProfileLocation(searchParams);
    target = {
      tab: nextTab,
      // Re-selecting the active group keeps the current section; switching
      // groups always lands on that group's default section.
      section: current.tab === nextTab ? current.section : projectProfileDefaultSection(nextTab),
    };
  } else {
    target = PROJECT_PROFILE_LEGACY_TAB_MAP[nextTab] || { tab: 'overview', section: 'summary' };
  }
  return applyProjectProfileLocation(searchParams, target.tab, target.section);
}

export function buildProjectProfileSectionSearchParams(searchParams, nextSection) {
  const { tab } = resolveProjectProfileLocation(searchParams);
  return applyProjectProfileLocation(searchParams, tab, nextSection);
}

export function buildProjectProfileDeliverySectionSearchParams(searchParams, nextSection) {
  return applyProjectProfileLocation(searchParams, 'delivery', nextSection);
}

function buildProjectTabPath(projectId, tabOrLegacyKey) {
  const query = buildProjectProfileTabSearchParams(
    new URLSearchParams(),
    tabOrLegacyKey,
  ).toString();
  return query ? `/projects/${projectId}?${query}` : `/projects/${projectId}`;
}

function gateReviewRoute(projectId, gateId) {
  if (!projectId || !gateId) return null;
  return `/projects/${projectId}/gate-review/${gateId}`;
}

function isApprovedGateStatus(status) {
  return /approved|complete/i.test(String(status || ''));
}

function isClosedWorkItemStatus(status) {
  return /done|complete|closed/i.test(String(status || ''));
}

function isOpenRiskRecord(record) {
  const kind =
    record?.kind ||
    record?.recordKind ||
    (record?.dependencyType ? 'dependency' : record?.severity ? 'risk' : 'issue');
  return kind === 'risk' && record?.status !== 'resolved' && record?.status !== 'closed';
}

function isResolvedIssueStatus(status) {
  return /resolved|closed/i.test(String(status || ''));
}

function isPendingChangeApproval(status) {
  return /submitted|pending|in_review/i.test(String(status || ''));
}

function resolveChangeDisplayTitle(change) {
  return change?.title || change?.description || change?.id || 'Untitled change';
}

function resolveChangeScheduleDelta(change) {
  return change?.impactSummary?.scheduleDays ?? change?.impactSummary?.scheduleDeltaDays ?? 0;
}

function resolveChangeBudgetDeltaUsd(change) {
  const usdMillions =
    change?.impactSummary?.budgetDeltaUsdM ?? change?.requestedBudgetDeltaUsdM ?? null;
  if (usdMillions != null) return Number(usdMillions) * 1000000;
  return change?.impactSummary?.budgetDeltaUsd ?? change?.requestedBudgetDeltaUsd ?? 0;
}

function resolveChangeStaffingDelta(change) {
  const impact = change?.impactSummary || {};
  if (impact.staffingDeltaFte != null) return `${impact.staffingDeltaFte} FTE`;
  if (impact.staffingDeltaHours != null) return `${impact.staffingDeltaHours} hours`;
  if (change?.requestedHoursDelta != null) return `${change.requestedHoursDelta} hours`;
  return '0';
}

function humanizeFieldKey(value) {
  const spaced = String(value || '')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim();
  if (!spaced) return 'Field';
  return spaced.replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function formatConfiguredFieldValue(value) {
  if (Array.isArray(value)) return value.length ? value.join(', ') : 'Not provided';
  if (value === true) return 'Yes';
  if (value === false) return 'No';
  if (value == null || value === '') return 'Not provided';
  return String(value);
}

function buildConfiguredFieldRows(customFields, definitions) {
  const values = customFields && typeof customFields === 'object' ? customFields : {};
  const definitionList = Array.isArray(definitions) ? definitions : [];
  const rows = definitionList
    .filter((definition) => values[definition?.fieldKey] !== undefined)
    .map((definition) => ({
      key: definition.fieldKey,
      label: definition.label || humanizeFieldKey(definition.fieldKey),
      value: formatConfiguredFieldValue(values[definition.fieldKey]),
      masked: definition?.accessPolicy?.masked === true,
      maskReason: definition?.accessPolicy?.message || null,
    }));

  for (const [fieldKey, value] of Object.entries(values)) {
    if (rows.some((row) => row.key === fieldKey)) continue;
    rows.push({
      key: fieldKey,
      label: humanizeFieldKey(fieldKey),
      value: formatConfiguredFieldValue(value),
      masked: false,
      maskReason: null,
    });
  }

  return rows;
}

export function resolveProjectProfileGate(project, gates = []) {
  const gateList = Array.isArray(gates) ? gates : [];
  if (project?.currentGateId) {
    return (
      gateList.find((gate) => gate.id === project.currentGateId) || { id: project.currentGateId }
    );
  }
  return gateList.find((gate) => !isApprovedGateStatus(gate.status)) || gateList[0] || null;
}

// ---- ProjectProfilePage exception-count + health helpers -------------------
// All counts are derived from data ALREADY fetched by the profile page. A chip
// only renders when its count is > 0, so the tab bar never shows fabricated or
// "0" decoration — only real items owing leadership attention.

// Health sub-scores: schedule/cost/missionImpact read higher-is-better; risk is
// also stored higher-is-better in seeded data (a higher risk *score* = healthier
// risk posture), so no inversion is applied. We surface them as gauges only when
// the numeric field is present.
const PROJECT_HEALTH_DIMENSIONS = [
  { key: 'schedule', altKey: 'scheduleScore', label: 'Schedule' },
  { key: 'cost', altKey: 'costScore', label: 'Cost' },
  { key: 'risk', altKey: 'riskScore', label: 'Risk' },
  { key: 'missionImpact', altKey: 'missionImpactScore', label: 'Mission impact' },
];

function resolveHealthDimensionValue(hs, dimension) {
  const raw = hs?.[dimension.key] ?? hs?.[dimension.altKey];
  if (raw == null || raw === '' || Number.isNaN(Number(raw))) return null;
  return Number(raw);
}

function buildProjectHealthGauges(hs) {
  return PROJECT_HEALTH_DIMENSIONS.map((dimension) => ({
    ...dimension,
    value: resolveHealthDimensionValue(hs, dimension),
  })).filter((dimension) => dimension.value != null);
}

// Tab exception chips: count only the items that still owe a decision/action.
function deriveProjectTabExceptions({
  split,
  issues = [],
  changes = [],
  evmSnapshots = [],
  workItems = [],
  createdActions = [],
}) {
  const openRisks = (split?.risks || []).filter((risk) => isOpenRiskRecord(risk)).length;
  const openIssues = (Array.isArray(issues) ? issues : []).filter(
    (issue) => !isResolvedIssueStatus(issue.status),
  ).length;
  const pendingChanges = (Array.isArray(changes) ? changes : []).filter((change) =>
    isPendingChangeApproval(change.approvalState),
  ).length;
  const evmOffTrack = (Array.isArray(evmSnapshots) ? evmSnapshots : []).filter((snapshot) => {
    const cpi = Number(snapshot?.cpi);
    const spi = Number(snapshot?.spi);
    return (Number.isFinite(cpi) && cpi < 1) || (Number.isFinite(spi) && spi < 1);
  }).length;
  const openActions = [
    ...createdActions,
    ...(Array.isArray(workItems) ? workItems : []).filter((item) =>
      /action/i.test(String(item.type || item.itemType || '')),
    ),
  ].filter((item) => !isClosedWorkItemStatus(item.status)).length;

  return {
    risks: openRisks,
    // Governance flags any open issue, pending change, or off-track EVM line.
    governance: openIssues + pendingChanges + (evmOffTrack > 0 ? evmOffTrack : 0),
    actions: openActions,
  };
}

function tabExceptionTone(tabKey) {
  // Risk & Governance blockers read brick; pending delivery actions read amber.
  if (tabKey === 'risk-governance') return 'brick';
  return 'amber';
}

function ProfileSectionNav({ sections, active, onSelect, ariaLabel }) {
  if (!Array.isArray(sections) || sections.length <= 1) return null;
  return (
    <div
      className="row profile-delivery-section-nav"
      role="group"
      aria-label={ariaLabel}
      style={{ gap: 8, flexWrap: 'wrap', marginTop: 16 }}
    >
      {sections.map((sectionOption) => (
        <button
          key={sectionOption.key}
          type="button"
          className={`btn ${active === sectionOption.key ? 'primary' : 'ghost'} sm`}
          aria-pressed={active === sectionOption.key}
          onClick={() => onSelect(sectionOption.key)}
        >
          {sectionOption.label}
        </button>
      ))}
    </div>
  );
}

export function ProjectProfilePage() {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [riskActionDraft, setRiskActionDraft] = useState(null);
  const [createdActions, setCreatedActions] = useState([]);
  const riskPromotionMut = useMutation();
  const riskResolveMut = useMutation();
  const [pendingRiskResolution, setPendingRiskResolution] = useState(null);
  // Feature E — governed lifecycle stage change (advance / correct).
  const stageChangeMut = useMutation();
  const [pendingStageChange, setPendingStageChange] = useState(null);
  const { roles = [] } = useCurrentUser();
  const canSeeFinancials = rbac.canSeeRoute('/financials', roles);
  const canSeeAudit = rbac.canSeeRoute('/admin/audit', roles);
  const projectRes = useApi(`/projects/${id}`);
  const gates = useApi(`/projects/${id}/gates`);
  const risks = useApi(`/projects/${id}/risks`);
  const milestones = useApi(`/projects/${id}/milestones`);
  const fins = useApi(`/projects/${id}/financials`);
  const labor = useApi('/financials/labor-costs', {
    query: { scopeType: 'project', scopeId: id },
    skip: !canSeeFinancials,
  });
  const workItems = useApi(`/projects/${id}/work-items`);
  const sprints = useApi(`/projects/${id}/sprints`);
  const closeout = useApi(`/projects/${id}/closeout-package`);
  const kpis = useApi('/mission-kpis', { query: { scopeType: 'project', scopeId: id, limit: 50 } });
  const artifacts = useApi('/artifacts', {
    query: { scopeType: 'project', scopeId: id, limit: 50 },
  });
  const aiRuns = useApi('/ai/runs', { query: { scopeType: 'project', scopeId: id, limit: 50 } });
  const briefings = useApi('/briefings', {
    query: { scopeType: 'project', scopeId: id, limit: 20 },
  });
  const relatedEvidence = useApi(`/projects/${id}/related`);
  const auditEvents = useApi('/audit', {
    query: { targetType: 'Project', targetId: id, limit: 50 },
    skip: !canSeeAudit,
  });
  const healthChecks = useApi('/project-health-checks', {
    query: { projectId: id, limit: 20 },
  });
  const issues = useApi('/issues', {
    query: { scopeType: 'project', scopeId: id, limit: 50 },
  });
  const changeRequests = useApi('/change-requests', {
    query: { scopeType: 'project', scopeId: id, limit: 50 },
  });
  const evmSnapshots = useApi('/evm', {
    query: { scopeType: 'project', scopeId: id, limit: 20 },
  });
  const { tab, section } = resolveProjectProfileLocation(searchParams);
  const [originEvidence, setOriginEvidence] = useState(null);
  const [originEvidenceOpen, setOriginEvidenceOpen] = useState(false);
  const selectTab = (nextTab) => {
    setSearchParams(buildProjectProfileTabSearchParams(searchParams, nextTab), { replace: false });
  };
  const selectSection = (nextSection) => {
    setSearchParams(buildProjectProfileSectionSearchParams(searchParams, nextSection), {
      replace: false,
    });
  };

  const risksList = useMemo(() => risks.data?.data || [], [risks.data]);
  const splitRisks = useMemo(() => {
    const out = { risks: [], issues: [], deps: [] };
    for (const r of risksList) {
      const normalizedKind = String(r.kind || r.recordKind || '').toLowerCase();
      const inferredKind =
        normalizedKind ||
        (String(r.id || '').startsWith('DEP-')
          ? 'dependency'
          : String(r.id || '').startsWith('ISS-') || r.sourceRiskId
            ? 'issue'
            : r.dependencyType
              ? 'dependency'
              : r.severity
                ? 'risk'
                : 'issue');
      const k = inferredKind;
      if (k === 'risk') out.risks.push(r);
      else if (k === 'issue') out.issues.push(r);
      else out.deps.push(r);
    }
    return out;
  }, [risksList]);
  const workItemsList = useMemo(() => workItems.data?.data || [], [workItems.data]);
  const projectActions = useMemo(
    () =>
      [
        ...createdActions,
        ...workItemsList.filter((item) => /action/i.test(String(item.type || item.itemType || ''))),
      ].filter(
        (item, index, list) => list.findIndex((candidate) => candidate.id === item.id) === index,
      ),
    [createdActions, workItemsList],
  );

  const data = projectRes.data?.data;
  usePageMetadata({
    title: data?.name || 'Project',
    subtitle: data?.description || data?.summary || undefined,
  });

  useEffect(() => {
    if (data?.sourceRapidId) {
      markRapidGuideStep('project-origin');
    }
  }, [data?.sourceRapidId]);

  if (projectRes.loading) return <LoadingSkeleton rows={6} />;
  if (projectRes.error?.status === 404) return <EmptyState title="Project not found" />;
  if (projectRes.error) return <ErrorState error={projectRes.error} />;
  if (!data) return <EmptyState title="Project not found" />;

  const hs = data.healthScores || data.health || {};
  const fs = data.financialSummary || {};
  const gatesList = gates.data?.data || [];
  const milestonesList = (milestones.data?.data || []).slice();
  const finRecords = fins.data?.data || [];
  const adjustments = finRecords.filter((f) => f.period === 'forecast_adjustment');
  const monthly = finRecords.filter((f) => /^20\d\d-\d\d$/.test(String(f.period || '')));
  const laborPayload = labor.data?.data || {};
  const laborSummary = laborPayload.summary || {};
  const sprintsList = sprints.data?.data || [];
  const kpiList = kpis.data?.data || [];
  const aiRunsList = aiRuns.data?.data || [];
  const briefingsList = briefings.data?.data || [];
  const artifactsList = artifacts.data?.data || [];
  const relatedEvidenceData = relatedEvidence.data?.data || {};
  const auditEventsList =
    (auditEvents.data?.data || []).length > 0
      ? auditEvents.data?.data || []
      : relatedEvidenceData.auditEvents || [];

  const groundedIn = {
    route: 'project',
    routeLabel: 'Project Profile',
    entityType: 'project',
    entityId: id,
    entityLabel: data.name,
  };
  const visuals = data.visuals || {};
  const configuredProjectFields = buildConfiguredFieldRows(
    data.customFields,
    data.customFieldDefinitions,
  );
  const projectKpiCard = (cardId) => (visuals.kpiCards || []).find((card) => card.id === cardId);
  const plannedCard = projectKpiCard('project-planned');
  const actualCard = projectKpiCard('project-actual');
  const forecastCard = projectKpiCard('project-forecast');
  const missionCard = projectKpiCard('project-mission-value');
  const projectFinancialAccessPolicy = data.financialAccessPolicy || {};
  const projectFinancialAccessLimited = Boolean(projectFinancialAccessPolicy.accessLimited);
  const financeAccessLimited = Boolean(
    projectFinancialAccessLimited || fins.data?.accessLimited || laborPayload.accessLimited,
  );
  const financeAccessPolicy =
    fins.data?.accessPolicy || laborPayload.accessPolicy || projectFinancialAccessPolicy || null;
  const financeSourceNotes = fins.data?.sourceNotes || [];
  const resolvedGate = resolveProjectProfileGate(data, gatesList);
  const gateReviewHref = gateReviewRoute(id, resolvedGate?.id);
  // Feature E — lifecycle stage-change gating. One stage forward only (no
  // skipping); backwards correction is exceptional and role-gated (Executive
  // Sponsor / Portfolio Manager). Both hidden once the project is terminal.
  const activeStageId = projectLifecycleStageIdFor(data.lifecycleStage);
  const activeStageIndex = PROJECT_LIFECYCLE_STAGES.findIndex(
    (stage) => stage.id === activeStageId,
  );
  const currentStageDef = activeStageIndex >= 0 ? PROJECT_LIFECYCLE_STAGES[activeStageIndex] : null;
  const nextStageDef =
    activeStageIndex >= 0 ? PROJECT_LIFECYCLE_STAGES[activeStageIndex + 1] || null : null;
  const prevStageDef = activeStageIndex > 0 ? PROJECT_LIFECYCLE_STAGES[activeStageIndex - 1] : null;
  const projectIsArchived =
    /archived|completed|closed/i.test(String(data.status || '')) ||
    activeStageId === 'archived_completed';
  const canAdvanceStage = Boolean(nextStageDef) && !projectIsArchived;
  const canCorrectStage =
    !projectIsArchived &&
    Boolean(prevStageDef) &&
    (roles.includes(rbac.ROLE_IDS.EXECUTIVE_SPONSOR) ||
      roles.includes(rbac.ROLE_IDS.PORTFOLIO_MANAGER));
  const gateNotPassed =
    Boolean(resolvedGate) && !/passed|approved|complete/i.test(String(resolvedGate.status || ''));
  const legacyTabExceptions = deriveProjectTabExceptions({
    split: splitRisks,
    issues: issues.data?.data || [],
    changes: changeRequests.data?.data || [],
    evmSnapshots: evmSnapshots.data?.data || [],
    workItems: workItemsList,
    createdActions,
  });
  // Exception counts roll up onto the consolidated tab groups: open actions
  // flag Delivery; open risks + governance blockers flag Risk & Governance.
  const tabExceptions = {
    delivery: legacyTabExceptions.actions,
    'risk-governance': legacyTabExceptions.risks + legacyTabExceptions.governance,
  };
  const closeoutReadiness = closeout.data?.data?.readiness || null;
  const closeoutStatus = closeout.data?.data?.status || 'draft';

  async function promoteProjectRiskToIssue(risk) {
    try {
      await riskPromotionMut.run(async () => {
        const response = await api.post(`/risks/${risk.id}/promote-to-issue`, {
          dueDate: risk.targetResolutionDate || null,
        });
        await Promise.all([risks.refresh(), issues.refresh()]);
        return { ...response, riskId: risk.id };
      });
      toast.success({
        title: 'Risk promoted to issue',
        body: risk.title || risk.id,
      });
    } catch (error) {
      toast.error({
        title: 'Risk promotion failed',
        body: error.message,
      });
    }
  }

  // Governed risk closure (intake → closeout lifecycle). PATCH /risks/:id with a
  // resolved status + typed rationale; the API persists the note in the risk
  // payload and audits it (governance.routes.js risk.update). Refresh the risk
  // list and closeout package so the resolved chip and 0-open-risk closeout
  // readiness both reflect the change (knowledge.service.js buildCloseoutReadiness).
  async function resolveProjectRisk(risk, resolutionNote) {
    try {
      await riskResolveMut.run(async () => {
        const response = await api.patch(`/risks/${risk.id}`, {
          status: 'resolved',
          resolutionNote,
        });
        await Promise.all([risks.refresh(), closeout.refresh()]);
        return { ...response, riskId: risk.id };
      });
      toast.success({
        title: 'Risk marked resolved',
        body: risk.title || risk.name || risk.id,
      });
    } catch (error) {
      toast.error({
        title: 'Risk resolution failed',
        body: error.message,
      });
    }
  }

  // Feature E — governed lifecycle stage change. Mirrors resolveProjectRisk: a
  // single audited PATCH /projects/:id carrying the new stage label plus a typed
  // rationale (recorded on the audit trail, not the durable project payload), then
  // refresh the project + gates so the pipeline and gate cards reflect the move.
  async function changeProjectStage({ direction, targetStageLabel, note }) {
    try {
      await stageChangeMut.run(async () => {
        const response = await api.patch(`/projects/${id}`, {
          lifecycleStage: targetStageLabel,
          stageChangeRationale: note,
          stageChangeDirection: direction,
        });
        await Promise.all([projectRes.refresh(), gates.refresh()]);
        return response;
      });
      toast.success({
        title: direction === 'advance' ? 'Stage advanced' : 'Stage corrected',
        body: `${data.name || id} → ${targetStageLabel}`,
      });
    } catch (error) {
      toast.error({
        title: direction === 'advance' ? 'Stage advance failed' : 'Stage correction failed',
        body: error.message,
      });
    }
  }

  return (
    <>
      <Breadcrumbs
        items={prependWorkspaceBreadcrumb(
          [{ to: '/projects', label: 'Projects' }, { label: data.name }],
          searchParams,
        )}
      />
      {/* PRIME-SHELL-UX-001 revise 2: the resolved record title now renders once
          as the body h1 (it was topbar-only under the PageActionStrip pattern).
          Zero props — it reads the name/description registered via
          usePageMetadata above. */}
      <ShellPageHeader />
      <PageActionStrip
        filters={
          <>
            {/* HealthIndicator already conveys the rolled-up project status
               (At risk / Watch / On track). The StatusChip rendered the same
               word a second time because `healthScores.overall` (a 0-100
               score) and `data.status` (an enum like "atRisk") both map to
               the same label via different helpers — a guaranteed duplicate
               for the current seeded data. Drop StatusChip from this strip;
               health is the canonical status for the project profile. */}
            <HealthIndicator health={hs.overall || data.overallHealth} />
            <span className="chip outline sm">{data.programId || '—'}</span>
            <span className="chip outline sm">{data.portfolioId || '—'}</span>
          </>
        }
        actions={
          <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
            <Link
              className="btn ghost sm tactile-press"
              to={buildInformationPaperPath({ scopeType: 'project', scopeId: id })}
            >
              Draft Information Paper
            </Link>
            {gateReviewHref ? (
              <Link className="btn primary sm tactile-press" to={gateReviewHref}>
                Open Gate Review
              </Link>
            ) : null}
          </div>
        }
      />

      <section className="bento profile-bento">
        {/* RD-07 closure: ONE compact KPI hero band replaces the four
            full-width KPI rows that previously stacked before any content.
            Each card keeps its real payload series or renders no sparkline at
            all (AC-TRUST-2 / AC-P3M-4) — the fabricated client-side ramp is
            gone, and masked roles get none. */}
        <div className="span-12">
          <KpiHeroBand
            eyebrow="Project posture"
            columns={4}
            items={[
              {
                id: plannedCard?.id || 'project-planned',
                surface: 'project',
                label: plannedCard?.label || 'Planned',
                value: plannedCard?.valueLabel || `$${(fs.plannedBudgetUsdM ?? 0).toFixed(1)}M`,
                sparkline: projectFinancialAccessLimited ? [] : plannedCard?.series,
                sparklineTone: plannedCard?.sparklineTone || 'blue',
                footer: projectFinancialAccessLimited
                  ? projectFinancialAccessPolicy.message
                  : plannedCard?.richTooltip?.headlineValue?.secondary || undefined,
                richTooltip: plannedCard?.richTooltip || null,
              },
              {
                id: actualCard?.id || 'project-actual',
                surface: 'project',
                label: actualCard?.label || 'Expended',
                value: actualCard?.valueLabel || `$${(fs.actualSpendUsdM ?? 0).toFixed(1)}M`,
                footer: projectFinancialAccessLimited
                  ? projectFinancialAccessPolicy.message
                  : actualCard?.richTooltip?.headlineValue?.secondary ||
                    `${fs.plannedBudgetUsdM ? Math.round((fs.actualSpendUsdM / fs.plannedBudgetUsdM) * 100) : 0}% burn`,
                sparkline: projectFinancialAccessLimited ? [] : actualCard?.series,
                sparklineTone: actualCard?.sparklineTone || 'sage',
                richTooltip: actualCard?.richTooltip || null,
              },
              {
                id: forecastCard?.id || 'project-forecast',
                surface: 'project',
                label: forecastCard?.label || 'Forecast at completion',
                value:
                  forecastCard?.valueLabel || `$${(fs.forecastAtCompletionUsdM ?? 0).toFixed(1)}M`,
                delta: projectFinancialAccessLimited
                  ? undefined
                  : Number((fs.forecastVarianceUsdM ?? 0).toFixed(1)),
                deltaLabel: projectFinancialAccessLimited
                  ? projectFinancialAccessPolicy.message
                  : forecastCard?.richTooltip?.headlineValue?.secondary ||
                    `${(fs.forecastVarianceUsdM ?? 0) > 0 ? '+' : ''}${(fs.forecastVarianceUsdM ?? 0).toFixed(1)} M var`,
                deltaTone: projectFinancialAccessLimited
                  ? undefined
                  : (fs.forecastVarianceUsdM ?? 0) > 0
                    ? 'brick'
                    : 'sage',
                richTooltip: forecastCard?.richTooltip || null,
              },
              {
                id: missionCard?.id || 'project-mission-value',
                surface: 'project',
                label: missionCard?.label || 'Mission value',
                // A mission-value score with zero tracked KPIs has no supporting
                // evidence. The API seeds a baseline score (e.g. the factory's
                // default 74) even for freshly generated projects, so the card
                // would otherwise read as a confident "74" beside "0 KPIs
                // tracked" — a contradiction. Show an em dash until at least one
                // mission KPI backs the score, keeping the "0 KPIs tracked"
                // subtitle honest rather than presenting an unsupported number.
                value:
                  kpiList.length === 0
                    ? '—'
                    : missionCard?.valueLabel || (fs.missionValueScore ?? '—'),
                footer:
                  missionCard?.richTooltip?.headlineValue?.secondary ||
                  (fs.costValueCategory ? humanizeEnum(fs.costValueCategory) : '—'),
                richTooltip: missionCard?.richTooltip || null,
              },
            ]}
          />
        </div>

        <div className="span-12 card profile-tabs-card">
          <div
            className="row profile-tabbar-redesign"
            role="tablist"
            style={{ gap: 8, flexWrap: 'wrap' }}
          >
            {PROJECT_PROFILE_TABS.map((t) => {
              const exceptionCount = tabExceptions[t.key] || 0;
              return (
                <button
                  key={t.key}
                  role="tab"
                  aria-selected={tab === t.key}
                  className={`btn ${tab === t.key ? 'primary' : 'ghost'} sm profile-tab-trigger${
                    exceptionCount > 0 ? ' profile-tab-trigger--flagged' : ''
                  }`}
                  onClick={() => selectTab(t.key)}
                >
                  {t.label}
                  {exceptionCount > 0 ? (
                    /* Screen-reader accessible exception badge (AC-P3M-3): the
                       aria-label spells out what the raw count means, so the
                       tab announces e.g. "Delivery, 3 open items need
                       attention" instead of a bare number. */
                    <span
                      className={`profile-tab-exception tone-${tabExceptionTone(t.key)}`}
                      aria-label={`${exceptionCount} open ${
                        exceptionCount === 1 ? 'item needs' : 'items need'
                      } attention`}
                    >
                      {exceptionCount}
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>

          {tab === 'overview' && (
            <>
              <ProfileSectionNav
                sections={PROJECT_PROFILE_TAB_SECTIONS.overview}
                active={section}
                onSelect={selectSection}
                ariaLabel="Overview sections"
              />
              {section === 'summary' ? (
                <OverviewTab
                  projectId={id}
                  data={data}
                  hs={hs}
                  fs={fs}
                  resolvedGate={resolvedGate}
                  risksList={risksList}
                  milestonesList={milestonesList}
                  kpiList={kpiList}
                  workItemsList={workItemsList}
                  aiRunsList={aiRunsList}
                  configuredProjectFields={configuredProjectFields}
                  onOpenRapidEvidence={(evidence) => {
                    setOriginEvidence(evidence);
                    setOriginEvidenceOpen(true);
                  }}
                />
              ) : null}
              {section === 'stakeholders' ? <ProjectStakeholderReadiness project={data} /> : null}
            </>
          )}

          {tab === 'plan' && (
            <>
              <ProfileSectionNav
                sections={PROJECT_PROFILE_TAB_SECTIONS.plan}
                active={section}
                onSelect={selectSection}
                ariaLabel="Plan sections"
              />
              {section === 'lifecycle' ? (
                <>
                  <LifecycleTab
                    projectId={id}
                    project={data}
                    gates={gatesList}
                    milestones={milestonesList}
                    closeoutReadiness={closeoutReadiness}
                    closeoutStatus={closeoutStatus}
                    currentStageLabel={
                      currentStageDef?.label || data.lifecycleStage || 'Unspecified'
                    }
                    nextStageLabel={nextStageDef?.label || null}
                    prevStageLabel={prevStageDef?.label || null}
                    canAdvanceStage={canAdvanceStage}
                    canCorrectStage={canCorrectStage}
                    stageChanging={stageChangeMut.running}
                    onAdvanceStage={() =>
                      setPendingStageChange({
                        direction: 'advance',
                        currentLabel:
                          currentStageDef?.label || data.lifecycleStage || 'Unspecified',
                        nextLabel: nextStageDef?.label || null,
                      })
                    }
                    onCorrectStage={() =>
                      setPendingStageChange({
                        direction: 'correct',
                        currentLabel:
                          currentStageDef?.label || data.lifecycleStage || 'Unspecified',
                        prevLabel: prevStageDef?.label || null,
                      })
                    }
                  />
                  <ConfirmActionDialog
                    open={Boolean(pendingStageChange)}
                    title={
                      pendingStageChange?.direction === 'advance'
                        ? `Advance ${data.name || id} to ${pendingStageChange?.nextLabel}?`
                        : `Correct stage back to ${pendingStageChange?.prevLabel}?`
                    }
                    body={
                      pendingStageChange?.direction === 'advance'
                        ? `${
                            gateNotPassed
                              ? `${(() => {
                                  const label = String(
                                    resolvedGate?.name || resolvedGate?.id || 'The current gate',
                                  );
                                  const status = String(resolvedGate?.status || 'not yet approved')
                                    .replace(/([a-z])([A-Z])/g, '$1 $2')
                                    .replace(/[_-]+/g, ' ')
                                    .toLowerCase();
                                  return `${/^gate\b/i.test(label) ? '' : 'Gate '}${label} is ${status}`;
                                })()} — advancing before gate approval is recorded on the audit trail. `
                              : ''
                          }This moves the lifecycle ${pendingStageChange?.currentLabel} → ${
                            pendingStageChange?.nextLabel
                          }. The stage change and rationale are audited.`
                        : `This corrects the lifecycle back one stage ${pendingStageChange?.currentLabel} → ${pendingStageChange?.prevLabel}. Backward corrections are exceptional and are recorded on the audit trail with your rationale.`
                    }
                    confirmLabel={
                      pendingStageChange?.direction === 'advance'
                        ? 'Advance stage'
                        : 'Correct stage'
                    }
                    tone={pendingStageChange?.direction === 'advance' ? 'primary' : 'danger'}
                    requireNote
                    noteLabel={
                      pendingStageChange?.direction === 'advance'
                        ? 'Advancement rationale'
                        : 'Stage correction rationale'
                    }
                    notePlaceholder="Document the basis for this lifecycle stage change."
                    busy={stageChangeMut.running}
                    onCancel={() => setPendingStageChange(null)}
                    onConfirm={async (note) => {
                      const target = pendingStageChange;
                      setPendingStageChange(null);
                      if (!target) return;
                      await changeProjectStage({
                        direction: target.direction,
                        targetStageLabel:
                          target.direction === 'advance' ? target.nextLabel : target.prevLabel,
                        note,
                      });
                    }}
                  />
                </>
              ) : null}
              {section === 'roadmap' ? (
                <RoadmapTab milestones={milestonesList} project={data} />
              ) : null}
              {section === 'archive' ? <ProjectArchiveTab project={data} /> : null}
              {section === 'journey' ? <MissionThreadPanel projectId={id} /> : null}
            </>
          )}

          {tab === 'delivery' && (
            <>
              <ProfileSectionNav
                sections={PROJECT_PROFILE_TAB_SECTIONS.delivery}
                active={section}
                onSelect={selectSection}
                ariaLabel="Delivery sections"
              />
              {['summary', 'sprints', 'work-items', 'gates'].includes(section) ? (
                <DeliveryTab
                  id={id}
                  project={data}
                  gates={gatesList}
                  risksList={risksList}
                  workItems={workItemsList}
                  sprints={sprintsList}
                  section={section}
                />
              ) : null}
              {section === 'resources' ? <ProjectResourcesTab projectId={id} /> : null}
              {section === 'actions' ? <ProjectActionsTab actions={projectActions} /> : null}
            </>
          )}

          {tab === 'money' && (
            <FinanceTab
              projectId={id}
              summary={fs}
              monthly={monthly}
              adjustments={adjustments}
              all={finRecords}
              laborSummary={laborSummary}
              burnSeries={visuals.sparklineSeries?.burn || null}
              accessLimited={financeAccessLimited}
              accessPolicy={financeAccessPolicy}
              sourceNotes={financeSourceNotes}
            />
          )}

          {tab === 'risk-governance' && (
            <>
              <ProfileSectionNav
                sections={PROJECT_PROFILE_TAB_SECTIONS['risk-governance']}
                active={section}
                onSelect={selectSection}
                ariaLabel="Risk & Governance sections"
              />
              {section === 'risks' ? (
                <>
                  <RisksTab
                    split={splitRisks}
                    onCreateAction={(risk) => {
                      setRiskActionDraft(risk);
                    }}
                    onPromoteIssue={promoteProjectRiskToIssue}
                    promotingRiskId={riskPromotionMut.data?.riskId || null}
                    onResolve={(risk) => setPendingRiskResolution(risk)}
                    resolvingRiskId={riskResolveMut.data?.riskId || null}
                  />
                  <ConfirmActionDialog
                    open={Boolean(pendingRiskResolution)}
                    title={`Mark risk ${pendingRiskResolution?.id || ''} resolved?`}
                    body={`Resolving "${
                      pendingRiskResolution?.title || pendingRiskResolution?.name || ''
                    }" closes it out of the open risk register and counts toward project closeout readiness. The decision and rationale are audited.`}
                    confirmLabel="Mark resolved"
                    requireNote
                    noteLabel="Resolution rationale"
                    notePlaceholder="Document how this risk was retired or why it no longer applies."
                    busy={riskResolveMut.running}
                    onCancel={() => setPendingRiskResolution(null)}
                    onConfirm={async (note) => {
                      const target = pendingRiskResolution;
                      setPendingRiskResolution(null);
                      if (target) await resolveProjectRisk(target, note);
                    }}
                  />
                </>
              ) : null}
              {section === 'governance' ? (
                <GovernanceTab
                  projectId={id}
                  projectName={data.name}
                  risks={splitRisks.risks}
                  issues={issues.data?.data || []}
                  changes={changeRequests.data?.data || []}
                  evmSnapshots={evmSnapshots.data?.data || []}
                  healthChecks={healthChecks.data?.data || []}
                />
              ) : null}
              {section === 'kpis' ? <KpisTab kpis={kpiList} /> : null}
            </>
          )}

          {tab === 'knowledge' && (
            <>
              <ProjectKnowledgeRoomTab projectId={id} />
              {/* Closeout builder lives with Knowledge in the consolidated
                  workspace (FS-P3M-3/4 "Knowledge: knowledge room, closeout
                  builder"). The contextual /projects/:id/closeout route is
                  unchanged. */}
              <div
                className="card profile-overview-card"
                data-testid="project-knowledge-closeout-card"
                style={{ marginTop: 16 }}
              >
                <div className="eyebrow">Closeout builder</div>
                <strong className="profile-bignum">
                  {closeoutReadiness?.canApprove ? 'Ready for approval' : 'Approval blocked'}
                </strong>
                <div className="muted small">
                  Closeout package status: {closeoutStatus || 'draft'}
                </div>
                <div className="row" style={{ gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                  <StatusChip status={closeoutReadiness?.canApprove ? 'approved' : 'blocked'}>
                    {closeoutReadiness?.canApprove ? 'Ready for approval' : 'Approval blocked'}
                  </StatusChip>
                  <Link className="btn ghost sm" to={`/projects/${id}/closeout`}>
                    Open closeout builder
                  </Link>
                </div>
              </div>
            </>
          )}

          {tab === 'ai-products' && (
            <>
              <ProfileSectionNav
                sections={PROJECT_PROFILE_TAB_SECTIONS['ai-products']}
                active={section}
                onSelect={selectSection}
                ariaLabel="AI & Products sections"
              />
              {section === 'ai' ? (
                <AiTab
                  groundedIn={groundedIn}
                  projectId={id}
                  projectName={data.name}
                  riskRecords={splitRisks.risks}
                  aiRunsList={aiRunsList}
                  healthChecksList={healthChecks.data?.data || []}
                  artifactsList={artifactsList}
                />
              ) : null}
              {section === 'briefings' ? (
                <BriefingsTab
                  groundedIn={groundedIn}
                  briefingsList={briefingsList}
                  relatedBriefings={relatedEvidenceData.briefings || []}
                />
              ) : null}
              {section === 'audit' ? (
                <AuditTab
                  auditEvents={auditEventsList}
                  auditSummary={relatedEvidenceData.auditSummary || null}
                />
              ) : null}
            </>
          )}
        </div>
      </section>
      <RapidEvidenceDrawer
        open={originEvidenceOpen}
        onClose={() => setOriginEvidenceOpen(false)}
        evidence={originEvidence}
      />
      <RiskToActionDrawer
        open={Boolean(riskActionDraft)}
        onClose={() => setRiskActionDraft(null)}
        // Re-resolve the open risk from the freshest list by id so a background
        // risks refetch (new array identities) keeps the drawer populated with
        // live data, while falling back to the captured object if the record
        // has dropped out of the list — the drawer never loses its source risk.
        risk={risksList.find((entry) => entry.id === riskActionDraft?.id) || riskActionDraft}
        project={data}
        onCreated={(action) => {
          // IMP-003: refetch the risk register so the server's promotedActionId
          // stamp surfaces the "View action →" deep-link on the risk card (also
          // reconciles the 409 duplicate path, which calls onCreated(null)).
          risks.refresh?.();
          if (!action) return;
          setCreatedActions((current) => [
            action,
            ...current.filter((item) => item.id !== action.id),
          ]);
        }}
      />
    </>
  );
}

function LifecycleTab({
  projectId,
  project,
  gates,
  milestones,
  closeoutReadiness,
  closeoutStatus,
  currentStageLabel,
  nextStageLabel,
  prevStageLabel,
  canAdvanceStage = false,
  canCorrectStage = false,
  stageChanging = false,
  onAdvanceStage,
  onCorrectStage,
}) {
  const currentGate = resolveProjectProfileGate(project, gates);
  const currentGateRoute = gateReviewRoute(projectId, currentGate?.id);
  const nextMilestone =
    milestones.find(
      (milestone) => !/done|complete|closed/i.test(String(milestone?.status || '')),
    ) ||
    milestones[0] ||
    null;
  const completedMilestoneCount = milestones.filter((milestone) =>
    /done|complete|closed/i.test(String(milestone?.status || '')),
  ).length;
  const readinessSummary = closeoutReadiness?.summary || {};
  const lifecycleStagePipeline = buildProjectLifecycleStagePipeline(project.lifecycleStage);
  const selectedLifecycleStageId = projectLifecycleStageIdFor(project.lifecycleStage);

  return (
    <div className="profile-tab" style={{ marginTop: 16 }} data-testid="project-lifecycle-tab">
      <h3>Lifecycle posture</h3>
      <article
        className="card profile-overview-card"
        data-testid="project-lifecycle-stage-pipeline"
        style={{ marginBottom: 16 }}
      >
        <div className="eyebrow">Lifecycle stages</div>
        <div className="muted small" style={{ marginBottom: 12 }}>
          Contract stage taxonomy for the current project lifecycle path. Stage markers summarize
          posture; recorded transition reasons appear in Stage change history below.
        </div>
        <StagePipeline
          stages={lifecycleStagePipeline}
          variant="pipeline"
          selectedId={selectedLifecycleStageId}
        />
      </article>
      <div className="profile-delivery-summary-grid">
        <article className="card profile-overview-card" data-testid="project-current-stage-card">
          <div className="eyebrow">Current stage</div>
          {/* Guide-10 external UAT FINDING-006: the visible current stage, the
              highlighted pipeline stage, and the advance action all derive from
              the SAME canonical lifecycle stage — a raw stored value like
              "approval" must never contradict a highlighted "Scoping". */}
          <strong className="profile-bignum">{currentStageLabel || 'Unspecified'}</strong>
          <div className="muted small">
            Status {project.status || 'unassigned'} · Program {project.programId || '—'}
          </div>
          <div className="row" style={{ gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
            <StatusChip status={currentStageLabel || 'draft'} />
            <StatusChip status={project.status || 'draft'} />
          </div>
          {/* Feature E — governed stage change. Advance is one step forward only
              (no skipping); the role-gated Correct step goes one stage back. Both
              open a rationale-required ConfirmActionDialog in the parent. */}
          {canAdvanceStage || canCorrectStage ? (
            <div
              className="row"
              style={{ gap: 8, flexWrap: 'wrap', marginTop: 12 }}
              data-testid="project-stage-actions"
            >
              {canAdvanceStage ? (
                <button
                  type="button"
                  className="btn primary sm"
                  onClick={onAdvanceStage}
                  disabled={stageChanging}
                  data-testid="advance-stage-button"
                >
                  Advance stage: {currentStageLabel} → {nextStageLabel}
                </button>
              ) : null}
              {canCorrectStage ? (
                <button
                  type="button"
                  className="btn ghost sm"
                  onClick={onCorrectStage}
                  disabled={stageChanging}
                  data-testid="correct-stage-button"
                >
                  Correct stage → {prevStageLabel}
                </button>
              ) : null}
            </div>
          ) : null}
        </article>

        <article className="card profile-overview-card">
          <div className="eyebrow">Current gate</div>
          {currentGate ? (
            <>
              <strong className="profile-bignum">{currentGate.name || currentGate.id}</strong>
              <div className="muted small">{currentGate.decisionForumDate || '—'}</div>
              <div className="row" style={{ gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                <StatusChip status={currentGate.status} />
                {currentGateRoute ? (
                  <Link className="btn primary sm" to={currentGateRoute}>
                    Open Gate Review
                  </Link>
                ) : null}
              </div>
            </>
          ) : (
            <p className="muted">No lifecycle gate is currently recorded.</p>
          )}
        </article>

        <article className="card profile-overview-card">
          <div className="eyebrow">Milestone progression</div>
          {nextMilestone ? (
            <>
              <strong className="profile-bignum">
                {nextMilestone.title || nextMilestone.name}
              </strong>
              <div className="muted small">{nextMilestone.targetDate || '—'}</div>
              <div className="row" style={{ gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                <StatusChip status={nextMilestone.status} />
                <span className="chip outline sm">
                  {completedMilestoneCount}/{milestones.length} complete
                </span>
              </div>
            </>
          ) : (
            <p className="muted">No milestones are recorded for this lifecycle yet.</p>
          )}
        </article>

        <article className="card profile-overview-card">
          <div className="eyebrow">Closeout readiness</div>
          <strong className="profile-bignum">
            {closeoutReadiness?.canApprove ? 'Ready for approval' : 'Approval blocked'}
          </strong>
          <div className="muted small">Closeout package status: {closeoutStatus || 'draft'}</div>
          <div className="row" style={{ gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
            <StatusChip status={closeoutReadiness?.canApprove ? 'approved' : 'blocked'}>
              {closeoutReadiness?.canApprove ? 'Ready for approval' : 'Approval blocked'}
            </StatusChip>
            <Link className="btn ghost sm" to={`/projects/${projectId}/closeout`}>
              Open closeout builder
            </Link>
          </div>
        </article>
      </div>

      <section
        className="card lifecycle-history-panel"
        aria-label="Stage change history"
        style={{ marginTop: 16 }}
      >
        <div className="lifecycle-history-panel__heading">
          <div>
            <div className="eyebrow">Stage change history</div>
            <p className="muted small">
              Audited lifecycle moves and the reason recorded for each decision.
            </p>
          </div>
          <span className="chip outline sm">{project.lifecycleHistory?.length || 0} recorded</span>
        </div>
        {project.lifecycleHistory?.length ? (
          <ol className="lifecycle-history-list">
            {project.lifecycleHistory.map((event) => (
              <li key={event.id} className="lifecycle-history-entry">
                <div className="lifecycle-history-entry__route">
                  <strong>
                    {projectLifecycleStageLabelFor(event.fromStage)} →{' '}
                    {projectLifecycleStageLabelFor(event.toStage)}
                  </strong>
                  <StatusChip status={event.direction || 'advance'} />
                </div>
                <p>
                  {event.rationale || 'No rationale was recorded for this historical transition.'}
                </p>
                <div className="muted small lifecycle-history-entry__meta">
                  <span>Recorded by {event.changedByUserId || 'unknown user'}</span>
                  {event.occurredAt ? (
                    <time dateTime={event.occurredAt}>
                      {format.formatDate(event.occurredAt, { withTime: true })}
                    </time>
                  ) : null}
                </div>
              </li>
            ))}
          </ol>
        ) : (
          <p className="muted">
            No governed stage changes have been recorded for this project yet.
          </p>
        )}
      </section>

      <div className="bento" style={{ gap: 16, marginTop: 16 }}>
        <div className="span-6 card profile-overview-card">
          <div className="eyebrow">Missing artifact types</div>
          {(readinessSummary.missingArtifactTypes || []).length ? (
            <ul className="profile-stat-list">
              {readinessSummary.missingArtifactTypes.map((item) => (
                <li key={item}>
                  <span>{item}</span>
                  <strong>Required</strong>
                </li>
              ))}
            </ul>
          ) : (
            <p className="muted">All required artifact types are present.</p>
          )}
        </div>

        <div className="span-6 card profile-overview-card">
          <div className="eyebrow">Readiness pressure</div>
          <ul className="profile-stat-list">
            <li>
              <span>Missing record posture</span>
              <strong>{(readinessSummary.missingOfficialRecordMetadata || []).length}</strong>
            </li>
            <li>
              <span>Open actions</span>
              <strong>{readinessSummary.openActionCount || 0}</strong>
            </li>
            <li>
              <span>Open risks</span>
              <strong>{readinessSummary.openRiskCount || 0}</strong>
            </li>
            <li>
              <span>Lessons captured</span>
              <strong>{readinessSummary.lessonCount || 0}</strong>
            </li>
            <li>
              <span>Decisions captured</span>
              <strong>{readinessSummary.decisionCount || 0}</strong>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}

function OverviewTab({
  projectId,
  data,
  hs,
  fs,
  resolvedGate,
  risksList,
  milestonesList,
  kpiList,
  workItemsList,
  aiRunsList,
  configuredProjectFields,
  onOpenRapidEvidence,
}) {
  const navigate = useNavigate();
  const openRisksAll = risksList.filter((r) => isOpenRiskRecord(r));
  const openRisks = openRisksAll.slice(0, 3);
  const nextMs = milestonesList.find((m) => m.status !== 'done') || milestonesList[0];
  const nextGateRoute = gateReviewRoute(projectId, resolvedGate?.id);

  const openNextGate = () => {
    if (nextGateRoute) navigate(nextGateRoute);
  };

  const openNextGateFromKeyboard = (event) => {
    if (!nextGateRoute) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      navigate(nextGateRoute);
    }
  };

  const healthGauges = buildProjectHealthGauges(hs);
  const overallHealth = (() => {
    const raw = hs.overall ?? hs.overallScore ?? data.overallHealth;
    return raw == null || raw === '' || Number.isNaN(Number(raw)) ? null : Number(raw);
  })();
  // Mission value is a real, governed field — surface it on the profile front
  // page (previously only reachable on the bento KPI strip) so leadership sees
  // the mission worth alongside delivery health.
  const missionValueRaw = fs.missionValueScore ?? hs.missionImpact ?? hs.missionImpactScore ?? null;
  const missionValue =
    missionValueRaw == null || missionValueRaw === '' || Number.isNaN(Number(missionValueRaw))
      ? null
      : Number(missionValueRaw);

  return (
    <div className="profile-tab" style={{ marginTop: 16 }}>
      <div className="bento" style={{ gap: 16 }}>
        <div className="span-4 card profile-overview-card profile-health-card">
          <div className="row spread" style={{ alignItems: 'baseline' }}>
            <div className="eyebrow">Health scores</div>
            {overallHealth != null ? (
              <span className={`chip ${bandTone(overallHealth, { good: 80, warn: 50 })} sm`}>
                Overall {overallHealth}
              </span>
            ) : null}
          </div>
          {overallHealth != null ? (
            <div className="profile-health-overall">
              <InlineScoreCell
                value={overallHealth}
                max={100}
                thresholds={{ good: 80, warn: 50 }}
                ariaLabel={`Overall project health ${overallHealth} of 100`}
              />
            </div>
          ) : null}
          {healthGauges.length ? (
            <div className="profile-health-gauges">
              {healthGauges.map((dimension) => (
                <GaugeCard
                  key={dimension.key}
                  value={dimension.value}
                  min={0}
                  max={100}
                  label={dimension.label}
                  tone={bandTone(dimension.value, { good: 80, warn: 50 })}
                />
              ))}
            </div>
          ) : (
            <ul className="profile-health-list">
              {PROJECT_HEALTH_DIMENSIONS.map((dimension) => (
                <li key={dimension.key}>
                  <span>{dimension.label}</span>
                  <strong>—</strong>
                </li>
              ))}
            </ul>
          )}
          {missionValue != null ? (
            <div className="profile-health-mission">
              <span>Mission value</span>
              <InlineScoreCell
                value={missionValue}
                max={100}
                thresholds={{ good: 80, warn: 50 }}
                ariaLabel={`Mission value score ${missionValue} of 100`}
              />
              {fs.costValueCategory ? (
                <span className="muted small">{fs.costValueCategory}</span>
              ) : null}
            </div>
          ) : null}
        </div>
        <div
          className={`span-4 card profile-overview-card${nextGateRoute ? ' profile-overview-card--interactive' : ''}`}
          data-testid="project-overview-current-gate-card"
          data-gate-id={resolvedGate?.id || ''}
          role={nextGateRoute ? 'link' : undefined}
          tabIndex={nextGateRoute ? 0 : undefined}
          onClick={nextGateRoute ? openNextGate : undefined}
          onKeyDown={nextGateRoute ? openNextGateFromKeyboard : undefined}
        >
          <div className="eyebrow">Next gate</div>
          {resolvedGate ? (
            <>
              <strong className="profile-bignum">{resolvedGate.name || resolvedGate.id}</strong>
              <div className="muted small">{resolvedGate.decisionForumDate || '—'}</div>
              <StatusChip status={resolvedGate.status} />
              {nextGateRoute ? (
                <div className="row" style={{ marginTop: 8 }}>
                  <Link
                    className="btn primary sm"
                    to={nextGateRoute}
                    onClick={(event) => event.stopPropagation()}
                  >
                    Open Gate Review
                  </Link>
                </div>
              ) : null}
            </>
          ) : (
            <p className="muted">No gates recorded.</p>
          )}
        </div>
        <div className="span-4 card profile-overview-card">
          <div className="eyebrow">Next milestone</div>
          {nextMs ? (
            <>
              <strong className="profile-bignum">{nextMs.title || nextMs.name}</strong>
              <div className="muted small">{nextMs.targetDate || '—'}</div>
              <StatusChip status={nextMs.status} />
            </>
          ) : (
            <p className="muted">No milestones recorded.</p>
          )}
        </div>
        <div className="span-8 card profile-overview-card">
          <div className="eyebrow">Open risks ({openRisksAll.length})</div>
          {openRisks.length === 0 ? (
            <p className="muted">No open risks.</p>
          ) : (
            <div className="profile-open-risks-layout">
              <ul className="profile-risk-list">
                {openRisks.map((r) => (
                  <li key={r.id}>
                    <div className="row spread">
                      <strong>{r.title || r.name}</strong>
                      <StatusChip status={r.severity || r.status} />
                    </div>
                    <div className="muted small">{r.mitigationPlan?.slice(0, 96) || ''}</div>
                  </li>
                ))}
              </ul>
              {(() => {
                // Severity rollup across ALL open risks (not just the first 3
                // shown). Derived from the severity field already present on
                // each record — omitted entirely if no severities are set.
                const sevTone = (sev) => {
                  const s = String(sev || '').toLowerCase();
                  if (/crit|sev.?1|high/.test(s)) return 'brick';
                  if (/med|moderate|sev.?2/.test(s)) return 'amber';
                  if (/low|sev.?3|minor/.test(s)) return 'sage';
                  return 'slate';
                };
                const counts = openRisksAll.reduce((acc, risk) => {
                  const label = humanizeFieldKey(risk.severity || 'unspecified');
                  acc[label] = acc[label] || { label, count: 0, tone: sevTone(risk.severity) };
                  acc[label].count += 1;
                  return acc;
                }, {});
                const segments = Object.values(counts);
                return segments.length ? (
                  <StatusDonutMini
                    segments={segments}
                    size={92}
                    totalLabel="open"
                    ariaLabel="Open risk severity distribution"
                  />
                ) : null;
              })()}
            </div>
          )}
        </div>
        {data.sourceRapidId ? (
          <div
            className="span-4 card profile-overview-card"
            data-testid="project-originated-from-rapid-card"
          >
            <div className="eyebrow">Preview</div>
            <strong className="profile-bignum">
              {data.sourceRapidTitle || data.sourceRapidId}
            </strong>
            <div className="muted small">
              {data.sourceRapidPodId || '—'} · {data.sourceRapidDriver || 'Carryforward'}
            </div>
            <p className="muted small" style={{ marginTop: 8 }}>
              Preview. This project surface demonstrates how RAPID lineage, artifacts, metrics,
              decisions, and closeout context could carry forward inside PRIME.
            </p>
            <ul className="profile-stat-list">
              <li>
                <span>Decisions</span>
                <strong>
                  {(data.carriedForwardDecisionIds || data.rapidDecisionIds || []).length}
                </strong>
              </li>
              <li>
                <span>Artifacts</span>
                <strong>
                  {(data.carriedForwardArtifactIds || data.rapidLineageArtifactIds || []).length}
                </strong>
              </li>
              <li>
                <span>Metrics</span>
                <strong>
                  {(data.carriedForwardMetricIds || data.rapidMetricIds || []).length}
                </strong>
              </li>
            </ul>
            <div className="row" style={{ marginTop: 10, gap: 8, flexWrap: 'wrap' }}>
              <button
                type="button"
                className="btn ghost sm"
                onClick={() =>
                  onOpenRapidEvidence?.({
                    title: 'Project origin card',
                    summary:
                      'This project preserves source RAPID title, source pod, source driver, and carried-forward counts.',
                    source: data.sourceRapidId,
                    owner: data.ownerUserId || 'Graduated project owner',
                    confidence: 'High',
                    status: 'Carried forward from RAPID',
                    lastUpdated: data.updatedAt?.slice(0, 10) || 'Baseline',
                    relatedLinks: [
                      { href: `/rapids/${data.sourceRapidId}`, label: 'Open source RAPID' },
                    ],
                  })
                }
              >
                Open origin evidence
              </button>
              <Link className="btn primary sm" to={`/rapids/${data.sourceRapidId}`}>
                Open source RAPID
              </Link>
              <span className="chip outline sm">{data.sourceRapidId}</span>
            </div>
          </div>
        ) : null}
        {data.sourceIntakeRequestId ? (
          <div
            className="span-4 card profile-overview-card"
            data-testid="project-originated-from-intake-card"
          >
            <div className="eyebrow">Origin</div>
            <strong className="profile-bignum">
              {data.sourceIntakeRequestTitle || data.sourceIntakeRequestId}
            </strong>
            <p className="muted small" style={{ marginTop: 8 }}>
              Generated from an approved OWHA intake request. The full intake record — AI baseline
              scoring, human review, and the approval decision — remains the traceable origin of
              this project (AC-W1).
            </p>
            <div className="row" style={{ marginTop: 10, gap: 8, flexWrap: 'wrap' }}>
              <Link
                className="btn primary sm"
                to={`/project-factory/requests/${encodeURIComponent(data.sourceIntakeRequestId)}`}
              >
                Open intake request
              </Link>
              <span className="chip outline sm">{data.sourceIntakeRequestId}</span>
            </div>
          </div>
        ) : null}
        <div className="span-4 card profile-overview-card">
          <div className="eyebrow">Quick stats</div>
          <ul className="profile-stat-list">
            <li>
              <span>Work items</span>
              <strong>{workItemsList.length}</strong>
            </li>
            <li>
              <span>Milestones</span>
              <strong>{milestonesList.length}</strong>
            </li>
            <li>
              <span>KPIs</span>
              <strong>{kpiList.length}</strong>
            </li>
            <li>
              <span>AI runs</span>
              <strong>{aiRunsList.length}</strong>
            </li>
          </ul>
        </div>
        {configuredProjectFields.length ? (
          <div className="span-4 card profile-overview-card">
            <div className="eyebrow">Configured fields</div>
            <ul className="profile-stat-list">
              {configuredProjectFields.map((field) => (
                <li className="profile-configured-field" key={field.key}>
                  <div className="profile-configured-field-row">
                    <span>{field.label}</span>
                    <strong>{field.value}</strong>
                  </div>
                  {field.masked && field.maskReason ? (
                    <div className="profile-configured-field-note">{field.maskReason}</div>
                  ) : null}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
      {fs.sourceSystem || data.dataTrust?.sourceSystem ? (
        <div style={{ marginTop: 12 }}>
          <DataSourceBadge
            source={fs.sourceSystem || data.dataTrust?.sourceSystem}
            freshness={fs.dataTrust?.freshness || data.dataTrust?.freshness}
            confidence={fs.dataTrust?.confidence ?? data.dataTrust?.confidence}
          />
        </div>
      ) : null}
    </div>
  );
}

function DeliveryTab({ id, project, gates, risksList, workItems, sprints, section }) {
  const bucket = (status) => {
    const s = String(status || '').toLowerCase();
    if (/(todo|backlog|open|new|not.?started)/.test(s)) return 'todo';
    if (/block/.test(s)) return 'blocked';
    if (/review|qa|test/.test(s)) return 'review';
    if (/done|complete|closed/.test(s)) return 'done';
    return 'in_progress';
  };
  const cols = [
    { key: 'todo', label: 'To do' },
    { key: 'in_progress', label: 'In progress' },
    { key: 'review', label: 'Review' },
    { key: 'done', label: 'Done' },
  ];
  const activeSprint =
    sprints.find((sprint) => String(sprint.status || '').toLowerCase() === 'active') || null;
  const currentGate = resolveProjectProfileGate(project, gates);
  const currentGateRoute = gateReviewRoute(id, currentGate?.id);
  const openWorkItems = workItems.filter((workItem) => !isClosedWorkItemStatus(workItem.status));
  const grouped = useMemo(() => {
    const acc = { todo: [], in_progress: [], blocked: [], review: [], done: [] };
    for (const w of workItems) acc[bucket(w.status)].push(w);
    return acc;
  }, [workItems]);
  const blockedWorkItems = grouped.blocked || [];
  const openRisks = risksList.filter((risk) => isOpenRiskRecord(risk));
  const sprintPager = useClientPagination(sprints, {
    initialPageSize: 8,
    resetKey: id,
  });
  const gatePager = useClientPagination(gates, {
    initialPageSize: 10,
    resetKey: id,
  });

  const renderSummarySection = () => (
    <div className="profile-delivery-summary-grid">
      <article className="card profile-overview-card">
        <div className="eyebrow">Current Gate</div>
        {currentGate ? (
          <>
            <strong className="profile-bignum">{currentGate.name || currentGate.id}</strong>
            <div className="muted small">{currentGate.decisionForumDate || '—'}</div>
            <StatusChip status={currentGate.status} />
            {currentGateRoute ? (
              <div className="row" style={{ marginTop: 8 }}>
                <Link className="btn primary sm" to={currentGateRoute}>
                  Open Gate Review
                </Link>
              </div>
            ) : null}
          </>
        ) : (
          <p className="muted">No active gate</p>
        )}
      </article>
      <article className="card profile-overview-card">
        <div className="eyebrow">Active Sprint</div>
        {activeSprint ? (
          <>
            <strong className="profile-bignum">{activeSprint.name || activeSprint.id}</strong>
            <div className="muted small">
              {activeSprint.startDate || '—'} → {activeSprint.endDate || '—'}
            </div>
            <StatusChip status={activeSprint.status} />
          </>
        ) : (
          <p className="muted">No active sprint</p>
        )}
      </article>
      <article className="card profile-overview-card">
        <div className="eyebrow">Open Work Items</div>
        {openWorkItems.length ? (
          <>
            <strong className="profile-bignum">{openWorkItems.length}</strong>
            <div className="muted small">
              {blockedWorkItems.length} blocked · {grouped.review.length} in review
            </div>
          </>
        ) : (
          <p className="muted">No work items</p>
        )}
      </article>
      <article className="card profile-overview-card">
        <div className="eyebrow">Blockers &amp; Open Risks</div>
        {blockedWorkItems.length || openRisks.length ? (
          <>
            <strong className="profile-bignum">{blockedWorkItems.length + openRisks.length}</strong>
            <div className="muted small">
              {blockedWorkItems.length} blockers · {openRisks.length} open risks
            </div>
          </>
        ) : (
          <p className="muted">No active blockers or open risks</p>
        )}
      </article>
    </div>
  );

  const renderSprintsSection = () => (
    <>
      <h3>Sprints</h3>
      {sprints.length === 0 ? (
        <p className="muted">No sprints recorded.</p>
      ) : (
        <>
          <div className="profile-sprint-strip">
            {sprintPager.items.map((s) => (
              <span
                key={s.id}
                className={`chip ${s.status === 'active' ? 'blue' : s.status === 'completed' ? 'sage' : 'outline'} sm`}
              >
                {s.name || s.id} · {s.status}
              </span>
            ))}
          </div>
          <PageViewerControls
            page={sprintPager.page}
            label="sprints"
            pageSize={sprintPager.pageSize}
            onOffsetChange={sprintPager.setOffset}
            onPageSizeChange={sprintPager.setPageSize}
          />
        </>
      )}
    </>
  );

  const renderWorkItemsSection = () => (
    <>
      <h3>Work items ({workItems.length})</h3>
      {workItems.length === 0 ? (
        <EmptyState title="No work items" />
      ) : (
        <div className="profile-kanban">
          {cols.map((c) => (
            <ProfileWorkItemColumn key={c.key} column={c} items={grouped[c.key]} />
          ))}
        </div>
      )}
    </>
  );

  const renderGatesSection = () => (
    <>
      <h3>Gates ({gates.length})</h3>
      {gates.length === 0 ? (
        <p className="muted">No gates recorded.</p>
      ) : (
        <>
          <ul className="profile-gate-list">
            {gatePager.items.map((g) => (
              <li key={g.id} className="row spread">
                <Link to={gateReviewRoute(id, g.id)}>{g.name || g.id}</Link>
                <div className="row" style={{ gap: 6 }}>
                  <span className="muted small">{g.decisionForumDate || '—'}</span>
                  <StatusChip status={g.status} />
                </div>
              </li>
            ))}
          </ul>
          <PageViewerControls
            page={gatePager.page}
            label="gates"
            pageSize={gatePager.pageSize}
            onOffsetChange={gatePager.setOffset}
            onPageSizeChange={gatePager.setPageSize}
          />
        </>
      )}
    </>
  );

  return (
    <div className="profile-tab" style={{ marginTop: 16 }}>
      {section === 'summary' ? renderSummarySection() : null}
      {section === 'sprints' ? renderSprintsSection() : null}
      {section === 'work-items' ? renderWorkItemsSection() : null}
      {section === 'gates' ? renderGatesSection() : null}
    </div>
  );
}

function ProfileWorkItemColumn({ column, items }) {
  const pager = useClientPagination(items, {
    initialPageSize: 8,
    resetKey: column.key,
  });
  return (
    <div className="profile-kanban-col">
      <header>
        <strong>{column.label}</strong>
        <span className="chip outline sm">{items.length}</span>
      </header>
      <ul>
        {pager.items.map((w) => (
          <li key={w.id}>
            <div className="row spread">
              <strong>{w.title}</strong>
              <span className="chip outline sm">{w.priority || 'med'}</span>
            </div>
            <div className="muted small">
              {w.id} · {w.assignedToUserId || 'unassigned'}
            </div>
          </li>
        ))}
        {items.length === 0 ? <li className="muted small">—</li> : null}
      </ul>
      <PageViewerControls
        page={pager.page}
        label={`${column.label.toLowerCase()} work items`}
        pageSize={pager.pageSize}
        onOffsetChange={pager.setOffset}
        onPageSizeChange={pager.setPageSize}
      />
    </div>
  );
}

function RoadmapTab({ milestones, project }) {
  const lanes = useMemo(
    () => [
      {
        id: project.id,
        label: project.name,
        items: milestones
          .filter((m) => m.startDate && (m.targetDate || m.forecastDate))
          .map((m) => ({
            id: m.id,
            name: m.title || m.name,
            startDate: m.startDate,
            endDate: m.targetDate || m.forecastDate,
            progress: (m.percentComplete || 0) / 100,
            richTooltip: m.richTooltip || null,
            tone:
              m.status === 'done'
                ? 'sage'
                : m.status === 'atRisk'
                  ? 'amber'
                  : m.status === 'blocked'
                    ? 'brick'
                    : 'blue',
            milestones: [],
            __raw: m,
          })),
      },
    ],
    [milestones, project],
  );
  const milestonePager = useClientPagination(milestones, {
    initialPageSize: 10,
    resetKey: project.id,
  });
  return (
    <div className="profile-tab" style={{ marginTop: 16 }}>
      {milestones.length === 0 ? (
        <EmptyState title="No milestones" />
      ) : (
        <>
          <h3>Milestone timeline</h3>
          <GanttPanel lanes={lanes} height={Math.max(160, lanes[0].items.length * 28 + 80)} />
          <h3 style={{ marginTop: 16 }}>Milestone list</h3>
          <ul className="profile-milestone-list">
            {milestonePager.items.map((m) => (
              <li key={m.id}>
                <div className="row spread">
                  <strong>{m.title || m.name}</strong>
                  <StatusChip status={m.status} />
                </div>
                <div className="muted small">
                  {m.startDate} → {m.targetDate || m.forecastDate} · {m.percentComplete ?? 0}%
                  complete · {m.ownerUserId || 'unowned'}
                </div>
              </li>
            ))}
          </ul>
          <PageViewerControls
            page={milestonePager.page}
            label="milestones"
            pageSize={milestonePager.pageSize}
            onOffsetChange={milestonePager.setOffset}
            onPageSizeChange={milestonePager.setPageSize}
          />
        </>
      )}
    </div>
  );
}

function FinanceTab({
  projectId = null,
  summary,
  monthly,
  adjustments,
  all,
  burnSeries,
  laborSummary,
  accessLimited = false,
  accessPolicy = null,
  sourceNotes = [],
}) {
  const adjustmentPager = useClientPagination(adjustments, {
    initialPageSize: 5,
    resetKey: `adjustments-${adjustments.length}`,
  });
  const burnValues =
    burnSeries?.values ||
    monthly
      .slice()
      .sort((a, b) => String(a.period).localeCompare(String(b.period)))
      .map((m) => Number(m.actualSpendUsdM ?? m.amount ?? 0));
  const plannedBudgetUsdM = Number(summary?.plannedBudgetUsdM || 0);
  const obligatedUsdM = Number(summary?.obligatedUsdM || 0);
  const actualSpendUsdM = Number(summary?.actualSpendUsdM || 0);
  const forecastAtCompletionUsdM = Number(summary?.forecastAtCompletionUsdM || 0);
  const forecastVarianceUsdM = Number(summary?.forecastVarianceUsdM || 0);
  const burnRatePercent =
    summary?.burnRatePercent != null
      ? Number(summary.burnRatePercent)
      : plannedBudgetUsdM > 0
        ? Math.round((actualSpendUsdM / plannedBudgetUsdM) * 100)
        : 0;
  const sourceFreshness = humanizeFieldKey(
    summary?.dataTrust?.freshness || sourceNotes[0]?.freshness || 'unknown',
  );
  // AC-W9 — bind the source-posture zone to the newest governed financial record
  // for this project. A freshly committed ingestion record (carrying a current
  // dataTrust.lastUpdatedAt) becomes the newest record, so the consuming Money
  // tab honestly reflects the commit's record id and freshness timestamp.
  const latestGovernedRecord = (() => {
    const candidates = (Array.isArray(all) ? all : [])
      .filter((record) => record?.id)
      .map((record) => {
        const rawTimestamp =
          record.lastUpdatedAt ||
          record.dataTrust?.lastUpdatedAt ||
          record.data_trust?.lastUpdatedAt ||
          record.importedAt ||
          record.adjustmentDate ||
          record.createdAt ||
          null;
        const parsed = rawTimestamp ? new Date(rawTimestamp).getTime() : NaN;
        return {
          record,
          rawTimestamp,
          sortKey: Number.isFinite(parsed) ? parsed : null,
        };
      });
    if (!candidates.length) return null;
    const timestamped = candidates.filter((entry) => entry.sortKey != null);
    const chosen = timestamped.length
      ? timestamped.reduce((newest, entry) => (entry.sortKey > newest.sortKey ? entry : newest))
      : candidates[candidates.length - 1];
    return {
      id: chosen.record.id,
      timestamp: chosen.rawTimestamp,
      freshness: humanizeFieldKey(
        chosen.record.freshness ||
          chosen.record.dataTrust?.freshness ||
          chosen.record.data_trust?.freshness ||
          'current',
      ),
    };
  })();
  if (accessLimited) {
    return (
      <div className="profile-tab" style={{ marginTop: 16 }}>
        <div className="bento" style={{ gap: 16 }}>
          <div className="span-4 card profile-overview-card">
            <div className="eyebrow">Monthly burn posture</div>
            <strong className="profile-bignum">Masked</strong>
            <div className="muted small">
              {accessPolicy?.message || 'Detailed financial values are masked for your role.'}
            </div>
          </div>
          <div className="span-4 card profile-overview-card">
            <div className="eyebrow">Forecast adjustments</div>
            <strong className="profile-bignum">Masked</strong>
            <div className="muted small">
              {adjustments.length} governed adjustment records are available after role review.
            </div>
          </div>
          <div className="span-4 card profile-overview-card">
            <div className="eyebrow">Approved labor-hour cost</div>
            <strong className="profile-bignum">Masked</strong>
            <div className="muted small">
              Approved hours and labor-cost rollups are hidden for this access posture.
            </div>
          </div>
          <div className="span-12 card profile-overview-card">
            <div className="eyebrow">Governed financial source posture</div>
            {sourceNotes.length ? (
              <ul className="profile-stat-list" style={{ marginTop: 12 }}>
                {sourceNotes.map((note, index) => (
                  <li
                    key={`${note.sourceSystem || 'source'}-${note.freshness || 'freshness'}-${index}`}
                  >
                    <span title={note.sourceSystem || undefined}>
                      {sourceSystemLabel(note.sourceSystem) || 'Governed PRIME financial records'}
                    </span>
                    <strong>{humanizeFieldKey(note.freshness || 'current')}</strong>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="muted small">
                Source freshness and confidence remain available even when detailed values are
                masked.
              </p>
            )}
          </div>
        </div>
        <div className="card profile-overview-card" style={{ marginTop: 16 }}>
          <div className="eyebrow">Detailed records</div>
          <p className="muted small">
            Detailed project finance records stay masked in this view. Use a governed finance role
            to inspect line-item values.
          </p>
        </div>
      </div>
    );
  }
  // Executive cost waterfall: Planned baseline, the obligated/expended draw-down
  // steps, and the Forecast-at-completion pivot. Every value is a real governed
  // financial field; the chart is omitted when no spend posture exists.
  const waterfallItems = (() => {
    if (!(plannedBudgetUsdM > 0 || actualSpendUsdM > 0 || forecastAtCompletionUsdM > 0)) {
      return [];
    }
    const items = [{ id: 'planned', label: 'Planned', value: plannedBudgetUsdM, type: 'baseline' }];
    if (obligatedUsdM > 0) {
      items.push({
        id: 'obligated',
        label: 'Obligated',
        value: -(plannedBudgetUsdM - obligatedUsdM),
        type: 'delta',
      });
    }
    if (actualSpendUsdM > 0) {
      const prior = obligatedUsdM > 0 ? obligatedUsdM : plannedBudgetUsdM;
      items.push({
        id: 'expended',
        label: 'Expended',
        value: -(prior - actualSpendUsdM),
        type: 'delta',
      });
    }
    if (forecastAtCompletionUsdM > 0) {
      items.push({
        id: 'forecast',
        label: 'Forecast at completion',
        value: forecastAtCompletionUsdM,
        type: 'forecast',
      });
    }
    return items.length > 1 ? items : [];
  })();

  return (
    <div className="profile-tab" style={{ marginTop: 16 }}>
      {waterfallItems.length ? (
        <article className="card profile-overview-card profile-finance-waterfall-card">
          <div className="eyebrow">Cost execution waterfall ($ M)</div>
          <div className="muted small" style={{ marginBottom: 8 }}>
            Planned budget through obligated and expended draw-down to forecast at completion.
          </div>
          <FinancialWaterfall
            items={waterfallItems}
            height={260}
            ariaLabel="Project cost execution waterfall"
          />
        </article>
      ) : null}
      <div className="bento" style={{ gap: 16 }}>
        <div className="span-6 card profile-overview-card">
          <div className="eyebrow">Financial execution summary</div>
          <div className="muted">
            <div>
              <span>Planned</span>: {format.formatCurrencyMillions(plannedBudgetUsdM * 1_000_000)}
            </div>
            <div>
              <span>Obligated</span>: {format.formatCurrencyMillions(obligatedUsdM * 1_000_000)}
            </div>
            <div>
              <span>Expended</span>: {format.formatCurrencyMillions(actualSpendUsdM * 1_000_000)}
            </div>
            <div>
              <span>Forecast at completion</span>:{' '}
              {format.formatCurrencyMillions(forecastAtCompletionUsdM * 1_000_000)}
            </div>
            <div>
              <span>Variance</span>:{' '}
              {format.formatCurrencyMillions(forecastVarianceUsdM * 1_000_000)}
            </div>
            <div>
              <span>Burn rate</span>: {burnRatePercent}%
            </div>
            <div>
              <span>Source freshness</span>: {sourceFreshness}
            </div>
          </div>
        </div>
        <div className="span-6 card profile-overview-card">
          <div className="eyebrow">Monthly expended burn ({monthly.length} periods)</div>
          {burnValues.length ? (
            <Sparkline
              values={burnValues}
              pointPayloads={burnSeries?.pointPayloads || null}
              width={420}
              height={56}
              tone="sage"
              surface="project"
              seriesId="burn"
              entityLabel="Project burn"
            />
          ) : (
            <p className="muted">No monthly expended records.</p>
          )}
        </div>
        <div className="span-4 card profile-overview-card">
          <div className="eyebrow">Pending forecast adjustments</div>
          {adjustments.length === 0 ? (
            <p className="muted">No adjustments on file.</p>
          ) : (
            <>
              <ul className="profile-adjustment-list">
                {adjustmentPager.items.map((a) => (
                  <li key={a.id}>
                    <div className="row spread">
                      <strong title={a.adjustmentType || a.category || undefined}>
                        {humanizeEnum(a.adjustmentType || a.category)}
                      </strong>
                      <span className={`chip ${Number(a.deltaUsdM) > 0 ? 'brick' : 'sage'} sm`}>
                        {Number(a.deltaUsdM) > 0 ? '+' : ''}
                        {Number(a.deltaUsdM).toFixed(1)} $M
                      </span>
                    </div>
                    {a.reason ? <div className="muted small">{a.reason}</div> : null}
                    <div className="muted small">
                      {a.reviewStatus ? humanizeEnum(a.reviewStatus) : '—'} ·{' '}
                      {a.adjustmentDate || '—'}
                    </div>
                  </li>
                ))}
              </ul>
              <PageViewerControls
                page={adjustmentPager.page}
                label="forecast adjustments"
                pageSize={adjustmentPager.pageSize}
                onOffsetChange={adjustmentPager.setOffset}
                onPageSizeChange={adjustmentPager.setPageSize}
              />
            </>
          )}
        </div>
        <div className="span-4 card profile-overview-card">
          <div className="eyebrow">Approved labor-hour cost</div>
          <strong className="profile-bignum">
            {format.formatCurrency(laborSummary?.laborCostEstimate || 0)}
          </strong>
          <div className="muted small">
            {format.formatNumber(laborSummary?.approvedHours || 0)} approved hours contributing to
            project financial posture.
          </div>
        </div>
        <div className="span-4 card profile-overview-card">
          <div className="eyebrow">Governed financial source posture</div>
          {/* AC-W3 / GAP B — the governed source badge for this project's budget. The
              same source/freshness/confidence + lineage target render on the LOE
              Workspace Budget posture and the allocation-conflict page so the three
              surfaces cannot disagree. Inert (no link) when no governed record exists. */}
          <div style={{ marginTop: 8 }}>
            <DataSourceBadge
              source={
                summary?.sourceSystem ||
                summary?.dataTrust?.sourceSystem ||
                sourceNotes[0]?.sourceSystem ||
                'PMRT'
              }
              freshness={summary?.dataTrust?.freshness || sourceNotes[0]?.freshness}
              confidence={summary?.dataTrust?.confidence ?? sourceNotes[0]?.confidence}
              lineageTargetId={summary?.representativeFinancialRecordId || null}
            />
          </div>
          {latestGovernedRecord ? (
            <div
              className="muted small"
              data-testid="finance-source-latest-record"
              style={{ marginTop: 8 }}
            >
              Latest governed record <strong className="mono">{latestGovernedRecord.id}</strong> ·{' '}
              {latestGovernedRecord.freshness}
              {latestGovernedRecord.timestamp
                ? ` · updated ${format.formatDate(latestGovernedRecord.timestamp, { withTime: true })}`
                : ''}
            </div>
          ) : null}
          {/* S3 — grounded "what changed this month?" copilot affordance. Self-hides
              when the project has no ingestion-committed finance change to explain. */}
          <div style={{ marginTop: 8 }}>
            <AskFinancialChangeButton projectId={projectId} />
          </div>
          {sourceNotes.length ? (
            <ul className="profile-stat-list" style={{ marginTop: 12 }}>
              {sourceNotes.map((note, index) => (
                <li
                  key={`${note.sourceSystem || 'source'}-${note.freshness || 'freshness'}-${index}`}
                >
                  <span title={note.sourceSystem || undefined}>
                    {sourceSystemLabel(note.sourceSystem) || 'Governed PRIME financial records'}
                  </span>
                  <strong>{humanizeFieldKey(note.freshness || 'current')}</strong>
                </li>
              ))}
            </ul>
          ) : (
            <p className="muted small">
              Source freshness and confidence stay visible so reviewers can confirm trusted finance
              updates after ingestion.
            </p>
          )}
        </div>
      </div>
      {(() => {
        // Require an actual numeric amount — a row without spend, delta,
        // or amount value carries no information and just renders as a
        // dash. Period alone is decorative, not data.
        const meaningful = all.filter(
          (f) => f.actualSpendUsdM != null || f.deltaUsdM != null || f.amountUsd != null,
        );
        return <FinanceRecordsTable records={meaningful} accessLimited={false} />;
      })()}
    </div>
  );
}

function FinanceRecordsTable({ records, accessLimited = false }) {
  const pager = useClientPagination(records, {
    initialPageSize: 10,
    resetKey: `finance-${records.length}`,
  });
  return (
    <>
      <h3 style={{ marginTop: 16 }}>Recent finance records ({records.length})</h3>
      {records.length === 0 ? (
        <EmptyState title="No financial records" />
      ) : (
        <>
          <table className="table profile-finance-records-table">
            <thead>
              <tr>
                <th>Period</th>
                <th>Category</th>
                <th>Δ / amount</th>
                <th>Source</th>
              </tr>
            </thead>
            <tbody>
              {pager.items.map((f) => (
                <tr key={f.id}>
                  <td>{f.period || f.fiscalYear || '—'}</td>
                  <td>{humanizeEnum(f.category || f.adjustmentType || 'summary')}</td>
                  <td className="mono">
                    {accessLimited
                      ? 'Masked'
                      : f.actualSpendUsdM != null
                        ? `$${Number(f.actualSpendUsdM).toFixed(2)}M`
                        : f.deltaUsdM != null
                          ? `${Number(f.deltaUsdM) > 0 ? '+' : ''}${Number(f.deltaUsdM).toFixed(1)} $M`
                          : f.amountUsd != null
                            ? `$${Number(f.amountUsd).toLocaleString()}`
                            : '—'}
                  </td>
                  <td className="small" title={f.sourceSystem || undefined}>
                    {sourceSystemLabel(f.sourceSystem) || '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <PageViewerControls
            page={pager.page}
            label="finance records"
            pageSize={pager.pageSize}
            onOffsetChange={pager.setOffset}
            onPageSizeChange={pager.setPageSize}
          />
        </>
      )}
    </>
  );
}

function GovernanceTab({
  projectId,
  projectName,
  risks,
  issues,
  changes,
  evmSnapshots,
  healthChecks,
}) {
  const mutation = useMutation();
  const createChangeMut = useMutation();
  const [localChanges, setLocalChanges] = useState(changes);
  // AC-GOV-4.1 parity with the governance Change Requests board: change
  // DECISIONS are reserved to change-board roles and confirm through the
  // explicit-consequence dialog with a required typed rationale. Non-board
  // users see the gate note instead of buttons that would 403 server-side.
  const { roles = [] } = useCurrentUser();
  const canDecideChanges = rbac.hasAnyRole(roles, rbac.CHANGE_BOARD_ROLES);
  const [pendingChangeDecision, setPendingChangeDecision] = useState(null); // { change, approvalState }
  const [draftChange, setDraftChange] = useState({
    title: '',
    changeType: 'schedule',
    description: '',
    requestedBudgetDeltaUsdM: '0',
    requestedHoursDelta: '0',
    requestedEndDate: '',
  });

  useEffect(() => {
    setLocalChanges(changes);
  }, [changes]);

  const latestSnapshot = useMemo(() => {
    const snapshots = Array.isArray(evmSnapshots) ? [...evmSnapshots] : [];
    snapshots.sort((left, right) =>
      String(right.period || '').localeCompare(String(left.period || '')),
    );
    return snapshots[0] || null;
  }, [evmSnapshots]);

  const latestHealthCheck = Array.isArray(healthChecks) ? healthChecks[0] || null : null;
  const openRiskCount = (Array.isArray(risks) ? risks : []).filter((risk) =>
    isOpenRiskRecord(risk),
  ).length;
  const openIssueCount = (Array.isArray(issues) ? issues : []).filter(
    (issue) => !isResolvedIssueStatus(issue.status),
  ).length;
  const pendingChangeCount = localChanges.filter((change) =>
    isPendingChangeApproval(change.approvalState),
  ).length;

  async function reviewChangeRequest(change, approvalState, decisionRationale) {
    try {
      const response = await mutation.run(() =>
        api.patch(`/change-requests/${change.id}`, { approvalState, decisionRationale }),
      );
      const updated = response?.data || response;
      setLocalChanges((current) =>
        current.map((record) => (record.id === change.id ? { ...record, ...updated } : record)),
      );
      toast.success({
        title: approvalState === 'approved' ? 'Change approved' : 'Change deferred',
        body: change.title || change.id,
      });
    } catch (error) {
      toast.error({
        title: 'Change review failed',
        body: error.message,
      });
    }
  }

  async function submitChangeRequest(event) {
    event.preventDefault();
    const title = draftChange.title.trim();
    if (!title) {
      toast.error({
        title: 'Change title required',
        body: 'Add a short title before submitting the change request.',
      });
      return;
    }
    try {
      const response = await createChangeMut.run(() =>
        api.post('/change-requests', {
          scopeType: 'project',
          scopeId: projectId,
          title,
          changeType: draftChange.changeType,
          description: draftChange.description.trim(),
          requestedBudgetDeltaUsdM: Number(draftChange.requestedBudgetDeltaUsdM || 0),
          requestedHoursDelta: Number(draftChange.requestedHoursDelta || 0),
          requestedEndDate: draftChange.requestedEndDate || null,
        }),
      );
      const created = response?.data || response;
      setLocalChanges((current) => [created, ...current.filter((row) => row.id !== created.id)]);
      setDraftChange({
        title: '',
        changeType: 'schedule',
        description: '',
        requestedBudgetDeltaUsdM: '0',
        requestedHoursDelta: '0',
        requestedEndDate: '',
      });
      toast.success({
        title: 'Change request submitted',
        body: created.title || created.id || projectName || projectId,
      });
    } catch (error) {
      toast.error({
        title: 'Change request failed',
        body: error.message,
      });
    }
  }

  return (
    <div className="profile-tab" style={{ marginTop: 16 }}>
      <h3>Governance summary</h3>
      <div className="profile-governance-grid">
        <article className="card profile-overview-card">
          <div className="eyebrow">Open risks</div>
          <strong className="profile-bignum">{format.formatNumber(openRiskCount)}</strong>
          <div className="muted small">
            Active project risks still requiring mitigation follow-through.
          </div>
        </article>
        <article className="card profile-overview-card">
          <div className="eyebrow">Open issues</div>
          <strong className="profile-bignum">{format.formatNumber(openIssueCount)}</strong>
          <div className="muted small">
            Promoted blockers requiring operational resolution and owner action.
          </div>
        </article>
        <article className="card profile-overview-card">
          <div className="eyebrow">Pending changes</div>
          <strong className="profile-bignum">{format.formatNumber(pendingChangeCount)}</strong>
          <div className="muted small">
            Board-ready changes awaiting approval or deferral before execution.
          </div>
        </article>
        <article className="card profile-overview-card">
          <div className="eyebrow">Latest EVM posture</div>
          <strong className="profile-bignum">
            {latestSnapshot
              ? `Cost performance (CPI) ${Number(latestSnapshot.cpi ?? 0).toFixed(2)}`
              : 'No snapshot'}
          </strong>
          <div className="muted small">
            {latestSnapshot
              ? `Schedule performance (SPI) ${Number(latestSnapshot.spi ?? 0).toFixed(2)} · ${latestSnapshot.period || 'current period'}`
              : 'Generate or import a project EVM snapshot to surface schedule and cost variance.'}
          </div>
        </article>
      </div>

      <div className="profile-governance-links">
        <Link className="btn ghost sm" to="/governance/issues">
          Open issue log
        </Link>
        <Link className="btn ghost sm" to="/governance/changes">
          Open change board
        </Link>
        <Link className="btn ghost sm" to="/governance/evm">
          Open PMO dashboard
        </Link>
        <Link className="btn ghost sm" to={buildProjectTabPath(projectId, 'ai')}>
          Open project AI workspace
        </Link>
      </div>

      <article className="card profile-overview-card" style={{ marginTop: 16 }}>
        <div className="row spread" style={{ gap: 12, alignItems: 'flex-start' }}>
          <div>
            <div className="eyebrow">Change initiation</div>
            <h4 style={{ margin: '4px 0 6px' }}>Create scoped change request</h4>
            <p className="muted small" style={{ margin: 0 }}>
              Capture schedule, budget, and staffing movement before it reaches the board.
            </p>
          </div>
          <StatusChip status="human_review_required" />
        </div>
        <form
          className="profile-governance-form"
          onSubmit={submitChangeRequest}
          style={{ marginTop: 16 }}
        >
          <label className="field">
            <span>Change title</span>
            <input
              value={draftChange.title}
              onChange={(event) =>
                setDraftChange((current) => ({ ...current, title: event.target.value }))
              }
            />
          </label>
          <label className="field">
            <span>Change type</span>
            <select
              value={draftChange.changeType}
              onChange={(event) =>
                setDraftChange((current) => ({ ...current, changeType: event.target.value }))
              }
            >
              <option value="schedule">Schedule</option>
              <option value="budget">Budget</option>
              <option value="staffing">Staffing</option>
              <option value="scope">Scope</option>
            </select>
          </label>
          <label className="field" style={{ gridColumn: '1 / -1' }}>
            <span>Rationale</span>
            <textarea
              rows={3}
              value={draftChange.description}
              onChange={(event) =>
                setDraftChange((current) => ({ ...current, description: event.target.value }))
              }
            />
          </label>
          <label className="field">
            <span>Requested budget delta (USD M)</span>
            <input
              type="number"
              step="0.1"
              value={draftChange.requestedBudgetDeltaUsdM}
              onChange={(event) =>
                setDraftChange((current) => ({
                  ...current,
                  requestedBudgetDeltaUsdM: event.target.value,
                }))
              }
            />
          </label>
          <label className="field">
            <span>Requested staffing delta (hours)</span>
            <input
              type="number"
              step="1"
              value={draftChange.requestedHoursDelta}
              onChange={(event) =>
                setDraftChange((current) => ({
                  ...current,
                  requestedHoursDelta: event.target.value,
                }))
              }
            />
          </label>
          <label className="field">
            <span>Requested end date</span>
            <input
              type="date"
              value={draftChange.requestedEndDate}
              onChange={(event) =>
                setDraftChange((current) => ({
                  ...current,
                  requestedEndDate: event.target.value,
                }))
              }
            />
          </label>
          <div className="row" style={{ gap: 8, justifyContent: 'flex-end', gridColumn: '1 / -1' }}>
            <button type="submit" className="btn primary sm" disabled={createChangeMut.running}>
              {createChangeMut.running ? 'Submitting…' : 'Submit change request'}
            </button>
          </div>
        </form>
      </article>

      <div className="profile-governance-grid">
        <article className="card profile-overview-card">
          <div className="row spread" style={{ gap: 12, alignItems: 'flex-start' }}>
            <div>
              <div className="eyebrow">Issue resolution board</div>
              <h4 style={{ margin: '4px 0 6px' }}>Tracked project issues</h4>
            </div>
            <span className="chip outline sm">{issues.length} records</span>
          </div>
          {issues.length === 0 ? (
            <p className="muted">No promoted issues currently tracked for this project.</p>
          ) : (
            <ul className="profile-governance-list">
              {issues.map((issue) => (
                <li key={issue.id} className="profile-governance-row">
                  <div className="row spread" style={{ gap: 12, alignItems: 'flex-start' }}>
                    <div>
                      <strong>{issue.title || issue.id}</strong>
                      <div className="muted small mono">
                        {issue.id} · owner {issue.ownerUserId || 'unassigned'} · due{' '}
                        {issue.dueDate || issue.targetResolutionDate || 'not set'}
                      </div>
                    </div>
                    <StatusChip status={issue.status || 'open'} />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </article>

        <article className="card profile-overview-card">
          <div className="row spread" style={{ gap: 12, alignItems: 'flex-start' }}>
            <div>
              <div className="eyebrow">Change review board</div>
              <h4 style={{ margin: '4px 0 6px' }}>Scoped delivery changes</h4>
            </div>
            <span className="chip outline sm">{localChanges.length} records</span>
          </div>
          {localChanges.length === 0 ? (
            <p className="muted">No submitted changes currently scoped to this project.</p>
          ) : (
            <ul className="profile-governance-list">
              {localChanges.map((change) => (
                <li key={change.id} className="profile-governance-row">
                  <div className="row spread" style={{ gap: 12, alignItems: 'flex-start' }}>
                    <div>
                      <div className="eyebrow">
                        {change.changeType ? humanizeEnum(change.changeType) : 'Change request'}
                      </div>
                      <strong>{resolveChangeDisplayTitle(change)}</strong>
                      <div className="muted small mono">{change.id}</div>
                    </div>
                    <StatusChip status={change.approvalState || 'submitted'} />
                  </div>
                  <div className="profile-governance-metrics">
                    <span>Schedule {resolveChangeScheduleDelta(change)} days</span>
                    <span>Budget {format.formatCurrency(resolveChangeBudgetDeltaUsd(change))}</span>
                    <span>Staffing {resolveChangeStaffingDelta(change)}</span>
                  </div>
                  {isPendingChangeApproval(change.approvalState) && canDecideChanges ? (
                    <div className="row" style={{ gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                      <button
                        type="button"
                        className="btn primary sm"
                        onClick={() =>
                          setPendingChangeDecision({ change, approvalState: 'approved' })
                        }
                        disabled={mutation.running}
                      >
                        Approve change
                      </button>
                      <button
                        type="button"
                        className="btn ghost sm"
                        onClick={() =>
                          setPendingChangeDecision({ change, approvalState: 'deferred' })
                        }
                        disabled={mutation.running}
                      >
                        Defer change
                      </button>
                    </div>
                  ) : isPendingChangeApproval(change.approvalState) ? (
                    <p className="muted small" data-testid="change-board-gate-note">
                      Pending change-board decision — deciding requires a board role.
                    </p>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </article>

        <article className="card profile-overview-card">
          <div className="eyebrow">Latest project health check</div>
          {latestHealthCheck ? (
            <>
              <strong className="profile-bignum">
                {humanizeFieldKey(latestHealthCheck.reviewState || 'pending_human_review')}
              </strong>
              <div className="muted small">
                {(latestHealthCheck.citationIds || []).length} citations attached
              </div>
              <div className="muted small mono">{latestHealthCheck.id}</div>
            </>
          ) : (
            <p className="muted">
              No governed health checks have been generated for this project yet.
            </p>
          )}
        </article>

        <article className="card profile-overview-card profile-evm-card">
          <div className="eyebrow">EVM detail</div>
          {latestSnapshot ? (
            <>
              <strong className="profile-bignum">
                {latestSnapshot.period || 'Current period'}
              </strong>
              {(() => {
                // CPI/SPI: 1.0 is on-baseline. Below 1.0 is over budget / behind
                // schedule (brick); 1.0+ is on/ahead (sage); within 2% is amber.
                const cpi = Number(latestSnapshot.cpi ?? 0);
                const spi = Number(latestSnapshot.spi ?? 0);
                const indexTone = (value) =>
                  value >= 1 ? 'sage' : value >= 0.98 ? 'amber' : 'brick';
                // Gauge scale 0–1.5 keeps the 1.0 baseline readable mid-arc.
                const indexValue = (value) => Math.max(0, Math.min(1.5, value));
                const costVariance = Number(latestSnapshot.costVariance ?? 0);
                const scheduleVariance = Number(latestSnapshot.scheduleVariance ?? 0);
                const varianceTone = costVariance < 0 || scheduleVariance < 0 ? 'brick' : 'sage';
                return (
                  <>
                    <div className="profile-evm-gauges">
                      <GaugeCard
                        value={Number(indexValue(cpi).toFixed(2))}
                        min={0}
                        max={1.5}
                        label="CPI"
                        sublabel="Cost performance"
                        tone={indexTone(cpi)}
                        deltaLabel={cpi >= 1 ? 'On / under cost' : 'Over cost'}
                        deltaTone={indexTone(cpi)}
                      />
                      <GaugeCard
                        value={Number(indexValue(spi).toFixed(2))}
                        min={0}
                        max={1.5}
                        label="SPI"
                        sublabel="Schedule performance"
                        tone={indexTone(spi)}
                        deltaLabel={spi >= 1 ? 'On / ahead' : 'Behind schedule'}
                        deltaTone={indexTone(spi)}
                      />
                    </div>
                    <div className="profile-evm-variance">
                      <div className="profile-evm-variance__head">
                        <span className="eyebrow">Cost &amp; schedule variance</span>
                        <span className="muted small">
                          Cost {format.formatNumber(costVariance)} · Schedule{' '}
                          {format.formatNumber(scheduleVariance)}
                        </span>
                      </div>
                      <MiniBars
                        values={[costVariance, scheduleVariance]}
                        tone={varianceTone}
                        ariaLabel={`Cost variance ${costVariance}, schedule variance ${scheduleVariance}`}
                      />
                    </div>
                  </>
                );
              })()}
            </>
          ) : (
            <p className="muted">No project EVM posture is currently available.</p>
          )}
        </article>
      </div>
      <ConfirmActionDialog
        open={Boolean(pendingChangeDecision)}
        title={`${pendingChangeDecision?.approvalState === 'approved' ? 'Approve' : 'Defer'} change ${pendingChangeDecision?.change?.id || ''}?`}
        body={
          pendingChangeDecision?.approvalState === 'approved'
            ? 'Approving clears this change for execution and applies its schedule, budget, and staffing deltas. The decision and rationale are audited.'
            : 'Deferring parks this change request without applying its deltas. The decision and rationale are audited.'
        }
        confirmLabel={
          pendingChangeDecision?.approvalState === 'approved'
            ? 'Confirm approval'
            : 'Confirm deferral'
        }
        tone={pendingChangeDecision?.approvalState === 'approved' ? 'primary' : 'danger'}
        requireNote
        noteLabel="Decision rationale"
        notePlaceholder="Document the board's basis for this change decision."
        busy={mutation.running}
        onCancel={() => setPendingChangeDecision(null)}
        onConfirm={async (note) => {
          const decision = pendingChangeDecision;
          setPendingChangeDecision(null);
          if (decision) await reviewChangeRequest(decision.change, decision.approvalState, note);
        }}
      />
    </div>
  );
}

// A risk is considered closed when its status matches the same resolved|closed
// semantics the closeout readiness gate uses (knowledge.service.js isClosedStatus).
function isRiskResolved(risk) {
  return /resolved|closed/i.test(String(risk?.status || ''));
}

function RisksTab({
  split,
  onCreateAction,
  onPromoteIssue,
  promotingRiskId,
  onResolve,
  resolvingRiskId,
}) {
  return (
    <div className="profile-tab" style={{ marginTop: 16 }}>
      <RisksColumn
        title="Risks"
        items={split.risks}
        onCreateAction={onCreateAction}
        onPromoteIssue={onPromoteIssue}
        promotingRiskId={promotingRiskId}
        onResolve={onResolve}
        resolvingRiskId={resolvingRiskId}
      />
      <RisksColumn title="Issues" items={split.issues} />
      <RisksColumn title="Dependencies" items={split.deps} />
    </div>
  );
}
function RisksColumn({
  title,
  items,
  onCreateAction,
  onPromoteIssue,
  promotingRiskId,
  onResolve,
  resolvingRiskId,
}) {
  const pager = useClientPagination(items, {
    initialPageSize: 5,
    resetKey: `${title}-${items.length}`,
  });
  return (
    <div style={{ marginTop: 16 }}>
      <h3>
        {title} ({items.length})
      </h3>
      {items.length === 0 ? (
        <p className="muted">None tracked.</p>
      ) : (
        <>
          <ul className="profile-risk-list">
            {pager.items.map((r) => {
              const resolved = isRiskResolved(r);
              return (
                <li key={r.id}>
                  <div className="row spread">
                    <strong>{r.title || r.name}</strong>
                    {/* A resolved risk surfaces its resolved status; open risks
                        keep leading with severity. */}
                    <StatusChip status={resolved ? r.status : r.severity || r.status} />
                  </div>
                  <div className="muted small">
                    {r.description?.slice(0, 140) || r.mitigationPlan?.slice(0, 140) || '—'}
                  </div>
                  <div className="muted small mono">
                    {r.id} · {r.ownerUserId || 'unowned'}
                  </div>
                  {title === 'Risks' && onCreateAction ? (
                    <div className="row" style={{ marginTop: 10, gap: 8, flexWrap: 'wrap' }}>
                      {/* IMP-003: once the risk has spawned an action (server stamps
                          promotedActionId) the card offers a "View action →"
                          deep-link instead of re-opening the create drawer, which
                          previously only refused on submit. */}
                      {r.promotedActionId ? (
                        <Link
                          className="chip sage sm"
                          to={`/actions/${r.promotedActionId}`}
                          data-testid={`risk-action-${r.id}`}
                        >
                          View action →
                        </Link>
                      ) : (
                        <button
                          type="button"
                          className="btn ghost sm"
                          onClick={() => onCreateAction(r)}
                        >
                          Create action
                        </button>
                      )}
                      {onPromoteIssue ? (
                        <button
                          type="button"
                          className="btn ghost sm"
                          onClick={() => onPromoteIssue(r)}
                          disabled={promotingRiskId === r.id}
                        >
                          {promotingRiskId === r.id ? 'Promoting…' : 'Promote to issue'}
                        </button>
                      ) : null}
                      {onResolve && !resolved ? (
                        <button
                          type="button"
                          className="btn ghost sm"
                          onClick={() => onResolve(r)}
                          disabled={resolvingRiskId === r.id}
                        >
                          {resolvingRiskId === r.id ? 'Resolving…' : 'Mark resolved'}
                        </button>
                      ) : null}
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
          <PageViewerControls
            page={pager.page}
            label={title.toLowerCase()}
            pageSize={pager.pageSize}
            onOffsetChange={pager.setOffset}
            onPageSizeChange={pager.setPageSize}
          />
        </>
      )}
    </div>
  );
}

function humanizeDirectionality(value) {
  if (!value) return '—';
  const spaced = String(value)
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim();
  if (!spaced) return value;
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
}

function KpisTab({ kpis }) {
  const pager = useClientPagination(kpis, {
    initialPageSize: 8,
    resetKey: `kpis-${kpis.length}`,
  });
  return (
    <div className="profile-tab" style={{ marginTop: 16 }}>
      {kpis.length === 0 ? (
        <EmptyState title="No mission KPIs" />
      ) : (
        <>
          <div className="profile-kpi-grid">
            {pager.items.map((k) => {
              const pct = k.targetValue
                ? Math.min(100, Math.max(0, Math.round((k.currentValue / k.targetValue) * 100)))
                : 0;
              return (
                <article key={k.id} className="profile-kpi-card">
                  <div className="eyebrow">{k.unit || '—'}</div>
                  <h4>{k.name}</h4>
                  <div className="profile-kpi-value mono">
                    {k.currentValue}
                    <span className="muted"> / {k.targetValue}</span>
                  </div>
                  <div className="score-bar">
                    <i
                      style={{ width: `${pct}%` }}
                      className={
                        k.status === 'onTrack' ? 'sage' : k.status === 'watch' ? 'amber' : 'brick'
                      }
                    />
                  </div>
                  <div className="muted small">
                    Baseline {k.baselineValue} · {humanizeDirectionality(k.directionality)}
                  </div>
                  <DataSourceBadge
                    source={k.sourceSystem}
                    freshness={k.dataTrust?.freshness}
                    confidence={k.dataTrust?.confidence}
                  />
                </article>
              );
            })}
          </div>
          <PageViewerControls
            page={pager.page}
            label="mission KPIs"
            pageSize={pager.pageSize}
            onOffsetChange={pager.setOffset}
            onPageSizeChange={pager.setPageSize}
          />
        </>
      )}
    </div>
  );
}

function AiTab({
  groundedIn,
  projectId,
  projectName,
  riskRecords,
  aiRunsList,
  healthChecksList,
  artifactsList,
}) {
  const healthMut = useMutation();
  const riskMitigationMut = useMutation();
  const lessonsMut = useMutation();
  const reviewRunMut = useMutation();
  const [localRuns, setLocalRuns] = useState(aiRunsList);
  const [generatedHealthCheck, setGeneratedHealthCheck] = useState(null);
  const [generatedRiskMitigation, setGeneratedRiskMitigation] = useState(null);
  const [generatedLessonsLearned, setGeneratedLessonsLearned] = useState(null);

  useEffect(() => {
    setLocalRuns(aiRunsList);
  }, [aiRunsList]);

  async function generateProjectHealthCheck() {
    try {
      const out = await healthMut.run(() =>
        api.post('/ai/project-health-check', {
          projectId,
        }),
      );
      const payload = out.data || out;
      setGeneratedHealthCheck(payload);
      setLocalRuns((current) => [
        {
          id: payload.runId,
          summary: `Drafted health check for ${projectId}`,
          reviewState: payload.reviewState,
          confidence: payload.confidence,
          citationIds: payload.citationIds || [],
          citations: payload.citations || [],
          status: 'completed',
        },
        ...current.filter((run) => run.id !== payload.runId),
      ]);
      toast.success({
        title: 'Project health check generated',
        body: `${projectName || projectId} is ready for human review.`,
      });
    } catch (e) {
      toast.error({ title: 'Health-check generation failed', body: e.message });
    }
  }

  const openRisk = riskRecords.find((risk) => isOpenRiskRecord(risk)) || riskRecords[0] || null;

  async function generateRiskMitigation() {
    if (!openRisk?.id) return;
    try {
      const out = await riskMitigationMut.run(() =>
        api.post('/ai/risk-mitigation', {
          riskId: openRisk.id,
        }),
      );
      const payload = out.data || out;
      const nextRecord = {
        riskId: openRisk.id,
        riskTitle: openRisk.title || openRisk.summary || openRisk.id,
        reviewState: payload.reviewState || 'pending_human_review',
        confidence: payload.confidence,
        recommendation: payload.recommendation || 'No mitigation recommendation returned.',
        guardrails: payload.guardrails || [],
        citationIds: payload.citationIds || [],
        citations: payload.citations || [],
        runId: payload.runId,
      };
      setGeneratedRiskMitigation(nextRecord);
      if (payload.runId) {
        setLocalRuns((current) => [
          {
            id: payload.runId,
            summary: `Drafted risk mitigation for ${projectName || projectId}`,
            reviewState: nextRecord.reviewState,
            citationIds: nextRecord.citationIds,
            citations: nextRecord.citations,
            status: 'completed',
          },
          ...current.filter((run) => run.id !== payload.runId),
        ]);
      }
      toast.success({
        title: 'Risk mitigation generated',
        body: `${nextRecord.riskTitle} is ready for human review.`,
      });
    } catch (e) {
      toast.error({ title: 'Risk mitigation failed', body: e.message });
    }
  }

  async function generateLessonsLearned() {
    try {
      const out = await lessonsMut.run(() =>
        api.post('/ai/lessons-learned', {
          projectId,
        }),
      );
      const payload = out.data || out;
      const nextRecord = {
        projectId,
        reviewState: payload.reviewState || 'pending_human_review',
        themes: payload.themes || [],
        lessons: payload.lessons || [],
        recommendedActions: payload.recommendedActions || [],
        citations: payload.citations || [],
        runId: payload.runId,
      };
      setGeneratedLessonsLearned(nextRecord);
      if (payload.runId) {
        setLocalRuns((current) => [
          {
            id: payload.runId,
            summary: `Drafted lessons learned for ${projectName || projectId}`,
            reviewState: nextRecord.reviewState,
            citationIds: (payload.citations || [])
              .map((citation) => citation.source)
              .filter(Boolean),
            citations: nextRecord.citations,
            status: 'completed',
          },
          ...current.filter((run) => run.id !== payload.runId),
        ]);
      }
      toast.success({
        title: 'Lessons-learned summary generated',
        body: `${projectName || projectId} is ready for human review.`,
      });
    } catch (e) {
      toast.error({ title: 'Lessons-learned generation failed', body: e.message });
    }
  }

  async function reviewGeneratedHealthCheck(action) {
    if (!generatedHealthCheck?.runId) return;
    try {
      const out = await reviewRunMut.run(() =>
        api.post(`/ai/runs/${generatedHealthCheck.runId}/review`, {
          action,
          note:
            action === 'approve_for_export'
              ? 'Approved from project AI tab.'
              : 'Sent back for rework from project AI tab.',
        }),
      );
      const payload = out.data || out;
      setGeneratedHealthCheck((current) =>
        current
          ? {
              ...current,
              reviewState: payload.reviewState || action,
            }
          : current,
      );
      setLocalRuns((current) =>
        current.map((run) =>
          run.id === generatedHealthCheck.runId
            ? { ...run, reviewState: payload.reviewState || action }
            : run,
        ),
      );
      toast.success({
        title:
          action === 'approve_for_export'
            ? 'Health check approved'
            : 'Health check sent for rework',
        body: generatedHealthCheck.projectName || projectName || projectId,
      });
    } catch (e) {
      toast.error({ title: 'Review update failed', body: e.message });
    }
  }

  const aiRunsToRender = localRuns;
  const renderedLessonRecommendations = new Set(
    (generatedLessonsLearned?.lessons || []).map((lesson) => lesson.recommendation).filter(Boolean),
  );
  const uniqueRecommendedActions = (generatedLessonsLearned?.recommendedActions || []).filter(
    (action) => !renderedLessonRecommendations.has(action),
  );
  const aiRunsPager = useClientPagination(aiRunsToRender, {
    initialPageSize: 5,
    resetKey: `ai-runs-${aiRunsToRender.length}`,
  });
  const artifactsPager = useClientPagination(artifactsList, {
    initialPageSize: 5,
    resetKey: `artifacts-${artifactsList.length}`,
  });

  return (
    <div className="profile-tab" style={{ marginTop: 16 }}>
      <AiGroundingChip groundedIn={groundedIn} />
      <div className="card" style={{ marginTop: 16 }}>
        <div className="row spread" style={{ gap: 12, alignItems: 'flex-start' }}>
          <div>
            <div className="eyebrow">Project AI health</div>
            <h3 style={{ margin: '4px 0 6px' }}>Generated project health check</h3>
            <p className="muted">
              Draft a cited schedule, budget, resource, risk/action, knowledge, and next-actions
              view for human review.
            </p>
          </div>
          <button
            type="button"
            className="btn primary sm"
            onClick={generateProjectHealthCheck}
            disabled={healthMut.running}
          >
            {healthMut.running ? 'Generating…' : 'Generate Project Health Check'}
          </button>
        </div>
        {generatedHealthCheck ? (
          <div style={{ marginTop: 16 }}>
            <div className="row" style={{ gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <StatusChip status={generatedHealthCheck.reviewState || 'pending_human_review'} />
              <span className="chip outline sm">
                {(generatedHealthCheck.citations || generatedHealthCheck.citationIds || []).length}{' '}
                citations attached
              </span>
              <span className="chip outline sm">
                Confidence {(generatedHealthCheck.confidence ?? 0).toFixed(2)}
              </span>
            </div>
            <div className="profile-kpi-grid" style={{ marginTop: 12 }}>
              {(generatedHealthCheck.sections || []).map((section) => (
                <article key={section.id} className="profile-kpi-card">
                  <div className="eyebrow">{section.title || humanizeFieldKey(section.id)}</div>
                  <p style={{ margin: '8px 0 0' }}>{section.body || 'No content yet.'}</p>
                </article>
              ))}
            </div>
            {(generatedHealthCheck.citations || []).length ? (
              <div style={{ marginTop: 12 }}>
                <strong>Source evidence</strong>
                <ul className="profile-airun-list" style={{ marginTop: 8 }}>
                  {generatedHealthCheck.citations.map((citation) => (
                    <li key={`${citation.source}-${citation.excerpt}`}>
                      <div className="row spread">
                        <strong>{citation.source}</strong>
                      </div>
                      <div className="muted small">{citation.excerpt}</div>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
            <div className="row" style={{ gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
              <button
                type="button"
                className="btn primary sm"
                onClick={() => reviewGeneratedHealthCheck('approve_for_export')}
                disabled={reviewRunMut.running}
              >
                Approve for use
              </button>
              <button
                type="button"
                className="btn ghost sm"
                onClick={() => reviewGeneratedHealthCheck('needs_rework')}
                disabled={reviewRunMut.running}
              >
                Send for rework
              </button>
              <Link
                className="btn ghost sm tactile-press"
                to={buildInformationPaperPath({ scopeType: 'project', scopeId: projectId })}
              >
                Draft Information Paper
              </Link>
            </div>
          </div>
        ) : healthChecksList.length ? (
          <div style={{ marginTop: 16 }}>
            <div className="eyebrow">Latest governed source</div>
            <p className="muted">
              {healthChecksList[0].id} · {healthChecksList[0].reviewState || 'pending_human_review'}{' '}
              · {(healthChecksList[0].citationIds || []).length} citations
            </p>
            <div className="row" style={{ gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
              <Link
                className="btn ghost sm tactile-press"
                to={buildInformationPaperPath({ scopeType: 'project', scopeId: projectId })}
              >
                Draft Information Paper
              </Link>
            </div>
          </div>
        ) : null}
      </div>
      <div className="card" style={{ marginTop: 16 }}>
        <div className="row spread" style={{ gap: 12, alignItems: 'flex-start' }}>
          <div>
            <div className="eyebrow">Project AI mitigation</div>
            <h3 style={{ margin: '4px 0 6px' }}>Generated risk mitigation</h3>
            <p className="muted">
              Draft a governed mitigation recommendation for the current open project risk posture.
            </p>
          </div>
          <button
            type="button"
            className="btn primary sm"
            onClick={generateRiskMitigation}
            disabled={riskMitigationMut.running || !openRisk}
          >
            {riskMitigationMut.running ? 'Generating…' : 'Generate risk mitigation'}
          </button>
        </div>
        {!openRisk ? (
          <p className="muted small" style={{ marginTop: 12 }}>
            No open project risks are available for AI mitigation drafting yet.
          </p>
        ) : generatedRiskMitigation ? (
          <div style={{ marginTop: 16 }}>
            <div className="row" style={{ gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <StatusChip status={generatedRiskMitigation.reviewState} />
              <span className="chip outline sm">{generatedRiskMitigation.riskTitle}</span>
              <span className="chip outline sm">
                Confidence {(generatedRiskMitigation.confidence ?? 0).toFixed(2)}
              </span>
            </div>
            <div className="profile-kpi-grid" style={{ marginTop: 12 }}>
              <article className="profile-kpi-card">
                <div className="eyebrow">Mitigation recommendation</div>
                <p style={{ margin: '8px 0 0' }}>{generatedRiskMitigation.recommendation}</p>
              </article>
              <article className="profile-kpi-card">
                <div className="eyebrow">Guardrails</div>
                {generatedRiskMitigation.guardrails.length ? (
                  <ul className="profile-stat-list" style={{ marginTop: 8 }}>
                    {generatedRiskMitigation.guardrails.map((guardrail) => (
                      <li key={guardrail}>
                        <span>{humanizeFieldKey(guardrail)}</span>
                        <strong>{guardrail}</strong>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="muted" style={{ margin: '8px 0 0' }}>
                    No additional guardrails returned.
                  </p>
                )}
              </article>
            </div>
            {(generatedRiskMitigation.citations || []).length ? (
              <div style={{ marginTop: 12 }}>
                <strong>Source evidence</strong>
                <ul className="profile-airun-list" style={{ marginTop: 8 }}>
                  {generatedRiskMitigation.citations.map((citation) => (
                    <li key={`${citation.source}-${citation.excerpt}`}>
                      <div className="row spread">
                        <strong>{citation.source}</strong>
                      </div>
                      <div className="muted small">{citation.excerpt}</div>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        ) : (
          <div style={{ marginTop: 12 }}>
            <div className="eyebrow">Current open risk</div>
            <p className="muted">
              {openRisk.title || openRisk.summary || openRisk.id} ·{' '}
              {openRisk.severity || 'severity pending'} · {openRisk.status || 'open'}
            </p>
          </div>
        )}
      </div>
      <div className="card" style={{ marginTop: 16 }}>
        <div className="row spread" style={{ gap: 12, alignItems: 'flex-start' }}>
          <div>
            <div className="eyebrow">Project AI retrospectives</div>
            <h3 style={{ margin: '4px 0 6px' }}>Generated lessons-learned summary</h3>
            <p className="muted">
              Draft reusable lessons and follow-on actions for the project manager to review.
            </p>
          </div>
          <button
            type="button"
            className="btn primary sm"
            onClick={generateLessonsLearned}
            disabled={lessonsMut.running}
          >
            {lessonsMut.running ? 'Generating…' : 'Generate lessons-learned summary'}
          </button>
        </div>
        {generatedLessonsLearned ? (
          <div style={{ marginTop: 16 }}>
            <div className="row" style={{ gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <StatusChip status={generatedLessonsLearned.reviewState} />
              <span className="chip outline sm">
                {(generatedLessonsLearned.citations || []).length} citations attached
              </span>
            </div>
            {generatedLessonsLearned.themes.length ? (
              <div style={{ marginTop: 12 }}>
                <strong>Themes</strong>
                <div className="row" style={{ gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                  {generatedLessonsLearned.themes.map((theme) => (
                    <span key={theme} className="chip outline sm">
                      {theme}
                    </span>
                  ))}
                </div>
              </div>
            ) : null}
            <div className="profile-kpi-grid" style={{ marginTop: 12 }}>
              {(generatedLessonsLearned.lessons || []).map((lesson) => (
                <article key={lesson.id || lesson.lesson} className="profile-kpi-card">
                  <div className="eyebrow">Lesson</div>
                  <p style={{ margin: '8px 0 0' }}>{lesson.lesson || 'No lesson captured.'}</p>
                  {lesson.recommendation ? (
                    <p className="muted small" style={{ marginTop: 10 }}>
                      {lesson.recommendation}
                    </p>
                  ) : null}
                </article>
              ))}
              {uniqueRecommendedActions.length ? (
                <article className="profile-kpi-card">
                  <div className="eyebrow">Recommended actions</div>
                  <ul className="profile-stat-list" style={{ marginTop: 8 }}>
                    {uniqueRecommendedActions.map((action, index) => (
                      <li key={`${action}-${index}`}>
                        <span>{action}</span>
                      </li>
                    ))}
                  </ul>
                </article>
              ) : null}
            </div>
            {(generatedLessonsLearned.citations || []).length ? (
              <div style={{ marginTop: 12 }}>
                <strong>Source evidence</strong>
                <ul className="profile-airun-list" style={{ marginTop: 8 }}>
                  {generatedLessonsLearned.citations.map((citation) => (
                    <li key={`${citation.source}-${citation.excerpt}`}>
                      <div className="row spread">
                        <strong>{citation.source}</strong>
                      </div>
                      <div className="muted small">
                        Excerpt available in the governed review record.
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
      <h3>AI runs ({aiRunsToRender.length})</h3>
      {aiRunsToRender.length === 0 ? (
        <p className="muted">No AI runs recorded for this project.</p>
      ) : (
        <>
          <ul className="profile-airun-list">
            {aiRunsPager.items.map((r) => (
              <li key={r.id}>
                <div className="row spread">
                  <strong>{r.summary || r.id}</strong>
                  <span className="chip outline sm">{r.agentId || '—'}</span>
                </div>
                <div className="muted small">
                  {r.completedAt?.slice(0, 10) || '—'} · {r.status || '—'} ·{' '}
                  {typeof r.confidence === 'number'
                    ? `conf ${r.confidence.toFixed(2)}`
                    : 'confidence pending'}
                </div>
                {r.citationIds?.length ? (
                  <div className="muted small">
                    {r.citationIds.length} citations · review{' '}
                    {r.reviewRequired ? 'required' : 'optional'}
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
          <PageViewerControls
            page={aiRunsPager.page}
            label="AI runs"
            pageSize={aiRunsPager.pageSize}
            onOffsetChange={aiRunsPager.setOffset}
            onPageSizeChange={aiRunsPager.setPageSize}
          />
        </>
      )}

      <h3 style={{ marginTop: 16 }}>Artifacts ({artifactsList.length})</h3>
      {artifactsList.length === 0 ? (
        <p className="muted">No artifacts indexed.</p>
      ) : (
        <>
          <EvidenceList artifacts={artifactsPager.items} />
          <PageViewerControls
            page={artifactsPager.page}
            label="artifacts"
            pageSize={artifactsPager.pageSize}
            onOffsetChange={artifactsPager.setOffset}
            onPageSizeChange={artifactsPager.setPageSize}
          />
        </>
      )}
    </div>
  );
}

function BriefingsTab({ groundedIn, briefingsList, relatedBriefings }) {
  const navigate = useNavigate();
  const exportMut = useMutation();
  const approveMut = useMutation();
  const [localBriefs, setLocalBriefs] = useState(briefingsList);

  useEffect(() => {
    setLocalBriefs(briefingsList);
  }, [briefingsList]);

  const briefingLinks = useMemo(() => {
    const explicitLinks = Array.isArray(relatedBriefings) ? relatedBriefings : [];
    if (explicitLinks.length) return explicitLinks;
    return localBriefs.map((brief) => ({
      id: brief.id,
      title: brief.title || brief.name || brief.id,
      status: brief.status || 'draft',
      route: `/briefings/${encodeURIComponent(brief.id)}`,
    }));
  }, [localBriefs, relatedBriefings]);

  const briefingsPager = useClientPagination(localBriefs, {
    initialPageSize: 8,
    resetKey: `briefings-${localBriefs.length}`,
  });

  async function exportNow(briefing, format) {
    try {
      const out = await exportMut.run(() =>
        api.post(`/briefings/${briefing.id}/export`, { format }),
      );
      toast.success({
        title: `${format.toUpperCase()} export`,
        body: out.data?.outputPath || briefing.id,
      });
      activityRail.push({
        action: 'briefing.export',
        targetType: 'briefing',
        targetId: briefing.id,
        actorUserId: 'you',
        summary: `${format} export`,
      });
      setLocalBriefs((prev) =>
        prev.map((item) =>
          item.id === briefing.id
            ? {
                ...item,
                status: 'exported',
                format,
                outputPath: out.data?.outputPath,
                lastExportedAt: new Date().toISOString(),
              }
            : item,
        ),
      );
    } catch (error) {
      toast.error({ title: 'Export failed', body: error.message });
    }
  }

  async function approveNow(briefing) {
    try {
      await approveMut.run(() => api.post(`/briefings/${briefing.id}/approve`, {}));
      toast.success({ title: 'Briefing approved', body: briefing.title });
      activityRail.push({
        action: 'briefing.approve',
        targetType: 'briefing',
        targetId: briefing.id,
        actorUserId: 'you',
      });
      setLocalBriefs((prev) =>
        prev.map((item) => (item.id === briefing.id ? { ...item, status: 'approved' } : item)),
      );
    } catch (error) {
      toast.error({ title: 'Approve failed', body: error.message });
    }
  }

  return (
    <div className="profile-tab" style={{ marginTop: 16 }}>
      <AiGroundingChip groundedIn={groundedIn} />
      <div className="card" style={{ marginTop: 16 }}>
        <div className="row spread" style={{ gap: 12, alignItems: 'flex-start' }}>
          <div>
            <div className="eyebrow">Governed products</div>
            <h3 style={{ margin: '4px 0 6px' }}>Briefings</h3>
            <p className="muted">
              Review the project&apos;s generated Information Papers, status reports, decks, and
              other leadership-facing outputs.
            </p>
          </div>
          <span className="chip outline sm">{briefingLinks.length} products linked</span>
        </div>
        {briefingLinks.length ? (
          <ul className="profile-airun-list" style={{ marginTop: 16 }}>
            {briefingLinks.map((briefing) => (
              <li key={briefing.id}>
                <div className="row spread" style={{ gap: 12, alignItems: 'center' }}>
                  <Link to={briefing.route || `/briefings/${encodeURIComponent(briefing.id)}`}>
                    {briefing.title || briefing.id}
                  </Link>
                  <StatusChip status={briefing.status || 'draft'} />
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p className="muted" style={{ marginTop: 16 }}>
            No governed briefing products are linked to this project yet.
          </p>
        )}
      </div>

      <h3 style={{ marginTop: 16 }}>Product cards ({localBriefs.length})</h3>
      {localBriefs.length === 0 ? (
        <p className="muted">No briefings drafted for this project.</p>
      ) : (
        <>
          <div className="profile-briefing-grid">
            {briefingsPager.items.map((briefing) => (
              <BriefingExportCard
                key={briefing.id}
                briefing={briefing}
                onOpen={(item) => navigate(`/briefings/${item.id}`)}
                onExportHtml={(item) => exportNow(item, 'html')}
                onExportPdf={(item) => exportNow(item, 'pdf')}
                onApprove={approveNow}
                busy={exportMut.running || approveMut.running}
              />
            ))}
          </div>
          <PageViewerControls
            page={briefingsPager.page}
            label="briefings"
            pageSize={briefingsPager.pageSize}
            onOffsetChange={briefingsPager.setOffset}
            onPageSizeChange={briefingsPager.setPageSize}
          />
        </>
      )}
    </div>
  );
}

function AuditTab({ auditEvents, auditSummary }) {
  const auditPager = useClientPagination(auditEvents, {
    initialPageSize: 8,
    resetKey: `project-audit-${auditEvents.length}`,
  });
  const totalEvents = Number(auditSummary?.totalEvents || auditEvents.length || 0);

  return (
    <div className="profile-tab" style={{ marginTop: 16 }}>
      <div className="card">
        <div className="row spread" style={{ gap: 12, alignItems: 'flex-start' }}>
          <div>
            <div className="eyebrow">Governed traceability</div>
            <h3 style={{ margin: '4px 0 6px' }}>Audit</h3>
            <p className="muted">
              Review recent project audit events, export actions, and governance checkpoints tied to
              this workspace.
            </p>
          </div>
          <span className="chip outline sm">{totalEvents} audit events</span>
        </div>
        {auditSummary?.categories?.length ? (
          <div className="row" style={{ gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
            {auditSummary.categories.map((category) => (
              <span key={category.category} className="chip outline sm">
                {humanizeFieldKey(category.category)} · {category.count}
              </span>
            ))}
          </div>
        ) : null}
      </div>

      <h3 style={{ marginTop: 16 }}>Recent events</h3>
      {auditEvents.length === 0 ? (
        <p className="muted">No audit events are recorded for this project yet.</p>
      ) : (
        <>
          <ul className="profile-airun-list">
            {auditPager.items.map((event) => {
              const summary =
                event.payload?.summary ||
                event.summary ||
                event.message ||
                `${humanizeFieldKey(event.action || 'event')} ${event.result ? `(${event.result})` : ''}`.trim();
              return (
                <li key={event.id}>
                  <div className="row spread" style={{ gap: 12, alignItems: 'center' }}>
                    <strong>{summary}</strong>
                    <StatusChip status={event.result || event.category || 'recorded'} />
                  </div>
                  <div className="muted small">
                    {event.occurredAt?.slice(0, 10) ||
                      event.eventAt?.slice(0, 10) ||
                      event.createdAt?.slice(0, 10) ||
                      '—'}{' '}
                    · {humanizeFieldKey(event.action || event.category || 'audit event')}
                  </div>
                </li>
              );
            })}
          </ul>
          <PageViewerControls
            page={auditPager.page}
            label="audit events"
            pageSize={auditPager.pageSize}
            onOffsetChange={auditPager.setOffset}
            onPageSizeChange={auditPager.setPageSize}
          />
        </>
      )}
    </div>
  );
}
